import {
  ColumnHeightOutlined,
  ColumnWidthOutlined,
  DeleteOutlined,
  LeftOutlined,
  LockOutlined,
  RightOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import type { ResizeCallbackData } from 'react-resizable';
import { IconButton, ScopeButton } from '@monorepo/shared-lib';
import {
  DatePicker,
  Dropdown,
  Input,
  Segmented,
  Space,
  Spin,
  Switch,
  Table,
  Tooltip,
} from 'antd';
import type { MenuProps } from 'antd';
import { PwaIcon } from 'pwa/components/Icon';
import { PwaChip, PwaSheet } from 'pwa/components/ui';
import type { ColumnsType } from 'antd/es/table';
import { ReactComponent as IconQuestionMark } from 'assets/images/icons/iconQuestionMark.svg';
import { FormSelect } from 'components/Custom/selects';
import { ConfirmModal } from 'components/Custom/ConfirmModal';
import { AttachmentViewer } from 'components/Vat/Report/tables/transactions/components/AttachmentViewer/index';
import { useIntegratedDrawerToolbar } from 'containers/DrawerContainer';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import { formatTransactionTableDate } from 'i18n/calendarLabels';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getContactTypeOptions } from 'utils/display-enum-label';
import { Provider } from 'store/api/enums';
import type { ProviderPostStatus } from 'store/organisation/types';
import { turnDrawerOff } from 'store/app/actions';
import { usePwaBottomSheetDragClose } from 'utils/usePwaBottomSheetDragClose';
import {
  ReviewStatus,
  type Organisation,
  type TrackingCategory,
  type ValidatedExcelTransaction,
} from 'store/organisation/models';
import {
  getAllExcelTransactionsPage,
  getExcelDrawerTransactionsBatchSaving,
} from 'store/organisation/selectors';
import type { TaxRate } from 'store/returns/models/returnData';
import {
  getLastFetchedTransactionAttachments,
  getVatAttachmentLoading,
} from 'store/vat/selectors';
import { isTransactionItemsEnabled } from 'utils/feature-flag-utils';
import { showsQuantityAndUnitPriceColumns } from 'utils/get-provider-type';
import {
  formatTransactionAmountDisplay,
  formatTransactionNumericInputValue,
  parseLocaleNumber,
  toApiNumber,
  toApiQuantity,
  useNumberFormat,
} from 'utils/numberFormat';
import { formatFormattedAmountWithCurrency } from 'utils/currencyFormat';
import { PreferenceActions, type Preference } from 'utils/preferences';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { usePwaDropdownSentinel } from 'utils/usePwaDropdownSentinel';
import { getMapImportFieldLabel } from 'shared/utils/map-import-field-label';
import type {
  InterpretationRuleDraft,
  InterpretationRuleField,
  InterpretationRuleFieldOptions,
} from '../interpretation-rules/types';
import { CreateCaptureRuleIconLink } from '../interpretation-rules/capture-rule-icon-link';
import {
  applyAccountSelectionToTransactionFields,
  buildAccountNameByIdMapFromOptions,
  resolveAccountSelectEditState,
} from './account-display';
import {
  buildDrawerPerLineFieldOrder,
  buildDrawerTablePerLineFieldOrder,
  DOCUMENT_GROUP_SHARED_DRAWER_KEYS,
  DRAWER_EXCLUDED_FIELD_KEYS,
  DRAWER_TABLE_AMOUNT_FIELD_KEYS,
  DRAWER_TABLE_COLUMN_RESIZE_MIN_WIDTH,
  DRAWER_TABLE_HEADER_LABEL_I18N_KEYS,
  DRAWER_TABLE_LINE_INDEX_COLUMN_WIDTH,
  getDrawerLineTableMinWidth,
  resolveDrawerTableColumnWidth,
  DRAWER_TRACKING_FIELD_ORDER,
  getEditDrawerLabel,
  getEditDrawerOptionalKeys,
  EDIT_DRAWER_TOOLTIP_KEY,
  EDITABLE_ROW_KEYS,
  ENTITY_TRANSACTION_EDIT_DRAWER_LAYOUT_PREF,
  NUMERIC_COLUMN_KEYS,
  OPTIONAL_NUMERIC_COLUMN_KEYS,
  sanitiseQuantityInput,
  TRANSACTION_AMOUNT_FIELD_KEYS,
  READ_ONLY_TRANSACTION_TAB_COLUMN_KEYS,
  isEntityTransactionColumnReadOnly,
  type DocumentGroupSharedDrawerKey,
  type TransactionEditDrawerPerLineFieldKey,
} from './constants';
import { DrawerLineTableResizableTitle } from './drawer-line-table-resizable-title';
import {
  applyCalculatedGrossAndNet,
  applyTrackingOptionSelectionToTransactionFields,
  buildDrawerNavigationTableRecords,
  computeDrawerDocumentNavigation,
  filterContactOptionsForSourceType,
  hasRowChanges,
  isTransactionDeletedRow,
  normalizeTransactionEditDrawerColumnCount,
  notifyReceiptTaxEditWarning,
  resolveAttachmentViewerProviderLogoKey,
  resolveCalculatedGrossAndNet,
  resolveDrawerFieldTooltipTitle,
  resolveProviderExternalLinkForDocumentRows,
  sumDocumentDrawerAmountTotals,
  resolveTransactionTotalsCurrencyCode,
  TRANSACTION_EDIT_REQUIRED_FIELD_LABELS,
  transactionQualifiesForMarkAsReviewed,
  type TransactionDrawerBulkSaveResult,
  type TransactionDrawerFieldErrorsByTransactionId,
  type TransactionDrawerSavePayload,
  type TransactionEditDrawerColumnCount,
  type TransactionEditRequiredFieldKey,
} from './entity-transaction-helpers';
import {
  DEFAULT_TRANSACTION_EDIT_DRAWER_LAYOUT,
  resolveTransactionEditDrawerLayoutFromPreferences,
  type TransactionDrawerLineViewMode,
  type TransactionEditDrawerDensity,
} from './preferences';
import {
  buildTrackingOptionSelectOptions,
  resolveTrackingOptionDisplayLabel,
  trackingSelectOptionsForValue,
} from './tracking-display';
import {
  TransactionEditModeSegmented,
  type TransactionEditMode,
} from './settings-drawer';
import {
  DRAWER_DOCUMENT_SURFACE_BACKGROUND,
  DRAWER_DOCUMENT_SURFACE_BORDER,
  DRAWER_DOCUMENT_SURFACE_RADIUS_PX,
  EditDrawerControlTooltipTarget,
  EditDrawerControlWrap,
  EditDrawerFieldStack,
  EditDrawerLabelTrail,
  EditDrawerLabelWithTooltip,
  EditDrawerLineFieldsColumn,
  EditDrawerLineFieldsGrid,
  EditDrawerQuestionMarkWrapper,
  EditDrawerSelectDropdownStyle,
  EditDrawerStackedLabel,
  TransactionEditDrawerLinesScroll,
  TransactionEditDrawerTableLayout,
  TransactionEditDrawerTableToolbar,
  TransactionEditDrawerTableWrap,
  TransactionEditDrawerDeleteRail,
  TransactionEditDrawerDeleteRailSlot,
  TransactionReviewDrawerSegmentedWrap,
} from './styles';
import {
  applyItemSelectionToTransactionFields,
  buildItemNameByIdMapFromOptions,
  resolveItemSelectEditState,
  type TransactionItemOption,
} from './item-display';
import {
  buildTaxNameByIdMap,
  buildTaxRateByIdMap,
  resolveTaxSelectEditState,
  syncTaxFieldsFromRateId,
} from './tax-display';
export type { TransactionDrawerLineViewMode } from './preferences';

const CREATE_RULE_FIELD_KEYS = new Set<InterpretationRuleField>([
  'contactName',
  'description',
  'accountCode',
  'taxName',
  'itemId',
]);

const OPTIONAL_TOOLTIP_RE = /\boptional\b/i;

function resolveEditDrawerFieldTooltipTitle(
  t: TFunction,
  fieldKey: string,
  isOptional: boolean
): string | null {
  const tooltipKey = EDIT_DRAWER_TOOLTIP_KEY[fieldKey];
  if (tooltipKey != null) {
    let text = t(`mapImportFields.tooltips.${tooltipKey}`);
    if (!isOptional && fieldKey === 'description') {
      text = t('organisation.extraction-rules.field-descriptions.description');
    }
    if (isOptional && !OPTIONAL_TOOLTIP_RE.test(text)) {
      return `${text} (optional)`;
    }
    return text;
  }
  if (isOptional) {
    return t('mapImportFields.tooltips.optionalFieldFallback');
  }
  return null;
}

type DrawerLineTableRow = {
  key: string;
  lineLabel: string;
  originalRow: ValidatedExcelTransaction;
  rowUuid: string;
};

/** Grouped option for Account Name dropdown (by account type). */
export type AccountNameGroupOption = {
  label: string;
  options: { label: string; value: string }[];
};

export interface TransactionEditDrawerProps {
  /** All lines to edit in this drawer (same document). Single-item is the previous behaviour. */
  transactions: ValidatedExcelTransaction[];
  /** Original rows when the drawer opened; used for review decisions. */
  reviewSeedTransactions?: ValidatedExcelTransaction[];
  /** Called with one entry per row; parent persists each (and closes drawer). */
  onBulkSave: (
    payloads: TransactionDrawerSavePayload[],
    /** Set only for PWA Save and Post. Empty object posts with no provider status. */
    postAfterSave?: { status?: ProviderPostStatus }
  ) => TransactionDrawerBulkSaveResult;
  onCancel: () => void;
  currencyOptions: { label: string; value: string }[];
  countryOptions: { label: string; value: string }[];
  sourceTypeOptions: { label: string; value: string }[];
  /** Flat options for Account Name (fallback when no grouped). */
  accountNameOptions: { label: string; value: string }[];
  /** Grouped by account type for Account Name dropdown (from chart of accounts). */
  accountNameOptionsGrouped: AccountNameGroupOption[];
  /** Item options (label = item name, value = item id) from organisation transaction items. */
  transactionItemOptions?: TransactionItemOption[];
  /** Tax name options (label = rate name, value = rate id) from organisation tax rates. */
  taxNameOptions: { label: string; value: string }[];
  /** Organisation tax rates (for syncing taxName / taxRate on selection). */
  organisationTaxRates?: TaxRate[];
  /** Contact options (id → label) for Name field. */
  contactOptions: { label: string; value: string }[];
  /** VIES contact id → ContactType key (CUSTOMER / SUPPLIER / BOTH) for QB/Zoho Name filtering. */
  contactTypeByContactId?: Map<string, string>;
  /** Provider name (e.g. Excel, Receipts); used to adjust account editing behaviour. */
  providerName?: string;
  /** Third-party bookkeeping: show tracking category selects per line (not Excel/Receipts). */
  showTrackingCategoryColumns?: boolean;
  trackingCategories?: TrackingCategory[];
  /**
   * When true, layout/view actions register into the drawer shell header (`DrawerData.integratedHeader`).
   */
  integratedDrawerHeader?: boolean;
  /** Previous/next in drawer header — document groups or single transactions. */
  documentNavigation?: {
    hasPrevious: boolean;
    hasNext: boolean;
    onNavigateToPrevious: () => void;
    onNavigateToNext: () => void;
    mode?: 'transaction' | 'document';
    /** 1-based index for PWA subtitle (Document X of Y). */
    currentIndex?: number;
    totalCount?: number;
  };
  /** PWA: open entity Activity sheet from document detail. */
  onActivity?: () => void;
  /** Show Drawer | Table segmented control beside column layout toggles. */
  showEditInDrawerSetting?: boolean;
  onEditModeChange?: (mode: TransactionEditMode) => void;
  handlePreferences?: (
    action: PreferenceActions,
    preference: Preference
  ) => unknown;
  /** Receipts / non-Excel uploads: rescan current document (opens parent confirm modal). */
  showRescan?: boolean;
  onRescan?: () => void;
  rescanLabel?: string;
  rescanTooltip?: string;
  onCreateInterpretationRule?: (
    draft: Partial<InterpretationRuleDraft>
  ) => void;
  onAfterSuccessfulSave?: (payloads: TransactionDrawerSavePayload[]) => void;
  /**
   * Delete a drawer line (same API as table row delete).
   * Shown when the document has more than one line.
   */
  onDeleteLine?: (transactionUuid: string) => void;
  /** Parent registers attachment-viewer close through the same leave guard as Close. */
  leaveGuardRef?: React.MutableRefObject<
    ((proceed: () => void) => void) | null
  >;
  /** PWA only: portal target div for the chrome (title/nav/actions) header */
  pwaChromePortalTarget?: HTMLDivElement | null;
  /** PWA only: portal target for sticky Rescan / Save action bar */
  pwaActionbarPortalTarget?: HTMLDivElement | null;
}

/** Drawer content: edit all transaction fields in a vertical form. */
export const TransactionEditDrawerContent: React.FC<
  TransactionEditDrawerProps
> = ({
  transactions,
  reviewSeedTransactions,
  onBulkSave,
  onCancel,
  currencyOptions,
  countryOptions,
  sourceTypeOptions,
  accountNameOptions,
  accountNameOptionsGrouped,
  transactionItemOptions = [],
  taxNameOptions,
  organisationTaxRates = [],
  contactOptions,
  contactTypeByContactId,
  providerName,
  showTrackingCategoryColumns = false,
  trackingCategories = [],
  integratedDrawerHeader = false,
  documentNavigation,
  onActivity,
  showEditInDrawerSetting = false,
  onEditModeChange,
  handlePreferences,
  showRescan = false,
  onRescan,
  rescanLabel,
  rescanTooltip,
  onCreateInterpretationRule,
  onAfterSuccessfulSave,
  onDeleteLine,
  leaveGuardRef,
  pwaChromePortalTarget,
  pwaActionbarPortalTarget,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const resolvedRescanLabel = rescanLabel ?? t('organisation.file-rescan');
  const [postSheetOpen, setPostSheetOpen] = React.useState(false);
  const [postAs, setPostAs] = React.useState<ProviderPostStatus>('DRAFT');
  const showTransactionItemColumns =
    showTrackingCategoryColumns && isTransactionItemsEnabled();
  const deriveGrossNetFromQuantityUnitPrice =
    showsQuantityAndUnitPriceColumns(providerName);
  const handleReceiptTaxFieldFocus = React.useCallback(() => {
    notifyReceiptTaxEditWarning({
      enabled: deriveGrossNetFromQuantityUnitPrice,
      dispatch,
      t,
    });
  }, [deriveGrossNetFromQuantityUnitPrice, dispatch, t]);
  const editDrawerOptionalKeys = React.useMemo(
    () => getEditDrawerOptionalKeys(providerName),
    [providerName]
  );
  const contactTypeOptions = React.useMemo(() => getContactTypeOptions(t), [t]);
  const { locale } = useNumberFormat();
  const isSaving = useSelector(getExcelDrawerTransactionsBatchSaving);
  const initialLayoutRef = React.useRef(
    handlePreferences
      ? resolveTransactionEditDrawerLayoutFromPreferences(handlePreferences)
      : DEFAULT_TRANSACTION_EDIT_DRAWER_LAYOUT
  );
  const [density, setDensity] = React.useState<TransactionEditDrawerDensity>(
    initialLayoutRef.current.density
  );
  const isCompact = density === 'compact';
  const pwaMobileNav = usePwaMobileNavEnabled();
  const getPwaPopupContainer = usePwaDropdownSentinel(pwaMobileNav);
  const [columnCount, setColumnCount] =
    React.useState<TransactionEditDrawerColumnCount>(
      initialLayoutRef.current.columnCount
    );
  const [lineViewMode, setLineViewMode] =
    React.useState<TransactionDrawerLineViewMode>(
      initialLayoutRef.current.lineViewMode
    );
  const [gridTextWrap, setGridTextWrap] = React.useState(
    initialLayoutRef.current.gridTextWrap === true
  );
  const [gridColumnWidths, setGridColumnWidths] = React.useState<
    Record<string, number>
  >(() => initialLayoutRef.current.gridColumnWidths ?? {});
  const [gridColumnWidthsLocked, setGridColumnWidthsLocked] = React.useState(
    initialLayoutRef.current.gridColumnWidthsUnlocked !== true
  );
  const isInitialLayoutMountRef = React.useRef(true);

  React.useEffect(() => {
    if (isInitialLayoutMountRef.current) {
      isInitialLayoutMountRef.current = false;
      return;
    }
    if (!handlePreferences) return;
    // Debounce so column-resize drags do not persist on every pixel.
    const timer = window.setTimeout(() => {
      handlePreferences(PreferenceActions.SET, {
        name: ENTITY_TRANSACTION_EDIT_DRAWER_LAYOUT_PREF,
        value: {
          density,
          columnCount: normalizeTransactionEditDrawerColumnCount(columnCount),
          lineViewMode,
          gridTextWrap,
          gridColumnWidths:
            Object.keys(gridColumnWidths).length > 0
              ? gridColumnWidths
              : undefined,
          gridColumnWidthsUnlocked: !gridColumnWidthsLocked,
        },
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [
    density,
    columnCount,
    lineViewMode,
    gridTextWrap,
    gridColumnWidths,
    gridColumnWidthsLocked,
    handlePreferences,
  ]);

  const handleDrawerTableColumnResize = React.useCallback(
    (fieldKey: string) =>
      (_e: React.SyntheticEvent, { size }: ResizeCallbackData) => {
        if (gridColumnWidthsLocked) return;
        const nextWidth = Math.max(
          DRAWER_TABLE_COLUMN_RESIZE_MIN_WIDTH,
          Math.min(640, Math.round(size.width))
        );
        setGridColumnWidths((prev) => {
          if (prev[fieldKey] === nextWidth) return prev;
          return { ...prev, [fieldKey]: nextWidth };
        });
      },
    [gridColumnWidthsLocked]
  );

  const resetDrawerTableColumnWidths = React.useCallback(() => {
    if (gridColumnWidthsLocked) return;
    setGridColumnWidths({});
  }, [gridColumnWidthsLocked]);
  const isAccountIdProviderDrawer =
    providerName?.toLowerCase() === Provider.RECEIPTS.toLowerCase() ||
    providerName?.toLowerCase() === Provider.EXCEL.toLowerCase();

  const accountNameByIdMap = React.useMemo(
    () =>
      buildAccountNameByIdMapFromOptions(
        accountNameOptions,
        accountNameOptionsGrouped
      ),
    [accountNameOptions, accountNameOptionsGrouped]
  );

  const itemNameByIdMap = React.useMemo(
    () => buildItemNameByIdMapFromOptions(transactionItemOptions),
    [transactionItemOptions]
  );

  /** COA dropdown uses row ids (Excel/Receipts/Xero/QBO) — same account edit rules for all. */
  const coaAccountOptionsUseIds = React.useMemo(
    () =>
      accountNameOptions.some(
        (o) =>
          accountNameByIdMap.has(String(o.value).trim()) ||
          accountNameByIdMap.has(String(o.value).trim().toLowerCase())
      ),
    [accountNameOptions, accountNameByIdMap]
  );

  const taxRateByIdMap = React.useMemo(
    () => buildTaxRateByIdMap(organisationTaxRates),
    [organisationTaxRates]
  );

  const taxNameByIdMap = React.useMemo(
    () => buildTaxNameByIdMap(organisationTaxRates),
    [organisationTaxRates]
  );

  const trackingCategoryFieldMeta = React.useMemo(() => {
    if (!showTrackingCategoryColumns) return [];
    return DRAWER_TRACKING_FIELD_ORDER.map((fieldKey, idx) => {
      const dimensionIndex = (idx === 0 ? 0 : 1) as 0 | 1;
      const fromApi = trackingCategories[idx]?.name?.trim();
      const label = fromApi || getEditDrawerLabel(fieldKey, t);
      const options = buildTrackingOptionSelectOptions(
        trackingCategories,
        dimensionIndex
      );
      return { fieldKey, label, options, dimensionIndex };
    });
  }, [showTrackingCategoryColumns, trackingCategories, t]);

  const trackingCategoryFieldMetaByKey = React.useMemo(
    () =>
      new Map(
        trackingCategoryFieldMeta.map((meta) => [meta.fieldKey, meta] as const)
      ),
    [trackingCategoryFieldMeta]
  );

  const [draftMap, setDraftMap] = React.useState<
    Record<string, Partial<ValidatedExcelTransaction>>
  >({});
  const [removedLineIds, setRemovedLineIds] = React.useState<Set<string>>(
    () => new Set()
  );
  const [linePendingDelete, setLinePendingDelete] = React.useState<
    string | null
  >(null);
  const lastSeededTransactionsRef = React.useRef<
    ValidatedExcelTransaction[] | null
  >(null);

  const [fieldErrorsByTransactionId, setFieldErrorsByTransactionId] =
    React.useState<TransactionDrawerFieldErrorsByTransactionId>({});

  React.useEffect(() => {
    const sameTransactionsRef =
      lastSeededTransactionsRef.current === transactions;
    if (isSaving) return;
    if (sameTransactionsRef) return;

    const next: Record<string, Partial<ValidatedExcelTransaction>> = {};
    for (const tx of transactions) {
      if (tx.uuid) next[String(tx.uuid)] = { ...tx };
    }
    lastSeededTransactionsRef.current = transactions;
    setRemovedLineIds(new Set());
    setLinePendingDelete(null);
    setDraftMap(next);
    setFieldErrorsByTransactionId({});
  }, [transactions, isSaving]);

  const visibleTransactions = React.useMemo(
    () =>
      transactions.filter(
        (tx) => tx.uuid && !removedLineIds.has(String(tx.uuid))
      ),
    [transactions, removedLineIds]
  );

  const clearFieldValidationError = React.useCallback(
    (
      rowUuid: string,
      fieldKey: TransactionEditRequiredFieldKey,
      applyToAllLines?: boolean
    ) => {
      setFieldErrorsByTransactionId((prev) => {
        if (!prev || Object.keys(prev).length === 0) return prev;
        const next = { ...prev };
        const rowIds = applyToAllLines ? Object.keys(next) : [rowUuid];
        let changed = false;
        for (const id of rowIds) {
          const current = next[id];
          if (!current?.includes(fieldKey)) continue;
          const filtered = current.filter((key) => key !== fieldKey);
          if (filtered.length === 0) {
            delete next[id];
          } else {
            next[id] = filtered;
          }
          changed = true;
        }
        return changed ? next : prev;
      });
    },
    []
  );

  const isDrawerFieldInvalid = React.useCallback(
    (
      rowUuid: string,
      fieldKey: TransactionEditRequiredFieldKey,
      applyToAllLines?: boolean
    ) => {
      const rowHasError = (id: string) =>
        (fieldErrorsByTransactionId[id] ?? []).includes(fieldKey);
      if (applyToAllLines) {
        return Object.keys(fieldErrorsByTransactionId).some(rowHasError);
      }
      return rowHasError(rowUuid);
    },
    [fieldErrorsByTransactionId]
  );

  const patchField = React.useCallback(
    (
      rowUuid: string,
      fieldKey: keyof ValidatedExcelTransaction,
      fieldValue: unknown
    ) => {
      setDraftMap((prev) => {
        const nextRow = {
          ...(prev[rowUuid] ?? {}),
          [fieldKey]: fieldValue,
        };
        const shouldDerive =
          deriveGrossNetFromQuantityUnitPrice &&
          (fieldKey === 'quantity' ||
            fieldKey === 'unitPrice' ||
            fieldKey === 'discountAmount' ||
            fieldKey === 'tax' ||
            fieldKey === 'taxInclusive');
        return {
          ...prev,
          [rowUuid]: shouldDerive
            ? applyCalculatedGrossAndNet(nextRow)
            : nextRow,
        };
      });
    },
    [deriveGrossNetFromQuantityUnitPrice]
  );

  /** Sync shared document fields to every line in the group. */
  const patchSharedField = React.useCallback(
    (fieldKey: keyof ValidatedExcelTransaction, fieldValue: unknown) => {
      setDraftMap((prev) => {
        const next = { ...prev };
        const shouldDerive =
          deriveGrossNetFromQuantityUnitPrice && fieldKey === 'taxInclusive';
        for (const tx of visibleTransactions) {
          const id = tx.uuid ? String(tx.uuid) : '';
          if (!id) continue;
          const nextRow = { ...(next[id] ?? {}), [fieldKey]: fieldValue };
          next[id] = shouldDerive
            ? applyCalculatedGrossAndNet(nextRow)
            : nextRow;
        }
        return next;
      });
    },
    [visibleTransactions, deriveGrossNetFromQuantityUnitPrice]
  );

  const isReviewableDrawerProvider =
    providerName?.toLowerCase() !== Provider.EXCEL.toLowerCase();
  const reviewSeedById = React.useMemo(
    () =>
      new Map(
        (reviewSeedTransactions ?? transactions)
          .filter((tx) => tx.uuid)
          .map((tx) => [String(tx.uuid), tx] as const)
      ),
    [reviewSeedTransactions, transactions]
  );

  const performSave = React.useCallback((
    postAfterSave?: { status?: ProviderPostStatus }
  ): boolean => {
    if (isSaving) return false;
    setFieldErrorsByTransactionId({});
    const payloads: TransactionDrawerSavePayload[] = [];
    for (const tx of visibleTransactions) {
      const id = tx.uuid;
      if (!id) continue;
      const draft = draftMap[String(id)] ?? {};
      const reviewSeed = reviewSeedById.get(String(id)) ?? tx;
      payloads.push({
        transactionId: String(id),
        original: tx,
        draft,
        markAsReviewed:
          isReviewableDrawerProvider &&
          transactionQualifiesForMarkAsReviewed(reviewSeed)
            ? true
            : undefined,
      });
    }
    const result = onBulkSave(payloads, postAfterSave);
    if (!result.ok) {
      setFieldErrorsByTransactionId(result.fieldErrorsByTransactionId);
      return false;
    }
    onAfterSuccessfulSave?.(payloads);
    return true;
  }, [
    visibleTransactions,
    draftMap,
    isReviewableDrawerProvider,
    onBulkSave,
    isSaving,
    reviewSeedById,
    onAfterSuccessfulSave,
  ]);

  const handleSubmit = React.useCallback(() => {
    performSave();
  }, [performSave]);

  const hasUnsavedChanges = React.useMemo(() => {
    for (const tx of visibleTransactions) {
      const id = tx.uuid;
      if (!id) continue;
      const draft = draftMap[String(id)] ?? {};
      if (hasRowChanges(draft, tx)) return true;
    }
    return false;
  }, [visibleTransactions, draftMap]);

  const handleConfirmDeleteLine = React.useCallback(() => {
    if (!linePendingDelete || !onDeleteLine) return;
    const uuid = linePendingDelete;
    setLinePendingDelete(null);
    setRemovedLineIds((prev) => {
      const next = new Set(prev);
      next.add(uuid);
      return next;
    });
    setDraftMap((prev) => {
      const next = { ...prev };
      delete next[uuid];
      return next;
    });
    setFieldErrorsByTransactionId((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, uuid)) return prev;
      const next = { ...prev };
      delete next[uuid];
      return next;
    });
    onDeleteLine(uuid);
  }, [linePendingDelete, onDeleteLine]);

  const [pendingLeave, setPendingLeave] = React.useState<{
    proceed: () => void;
  } | null>(null);

  const requestLeaveWithProceed = React.useCallback(
    (proceed: () => void) => {
      if (!hasUnsavedChanges) {
        proceed();
        return;
      }
      setPendingLeave({ proceed });
    },
    [hasUnsavedChanges]
  );

  React.useEffect(() => {
    if (!leaveGuardRef) return;
    leaveGuardRef.current = requestLeaveWithProceed;
    return () => {
      leaveGuardRef.current = null;
    };
  }, [leaveGuardRef, requestLeaveWithProceed]);

  const handleRequestClose = React.useCallback(() => {
    requestLeaveWithProceed(onCancel);
  }, [requestLeaveWithProceed, onCancel]);

  const handleEditModeChangeRequest = React.useCallback(
    (mode: TransactionEditMode) => {
      if (mode === 'table') {
        requestLeaveWithProceed(() => onEditModeChange?.('table'));
        return;
      }
      onEditModeChange?.(mode);
    },
    [requestLeaveWithProceed, onEditModeChange]
  );

  const guardedDocumentNavigation = React.useMemo(() => {
    if (!documentNavigation) return undefined;
    return {
      ...documentNavigation,
      onNavigateToPrevious: () => {
        requestLeaveWithProceed(documentNavigation.onNavigateToPrevious);
      },
      onNavigateToNext: () => {
        requestLeaveWithProceed(documentNavigation.onNavigateToNext);
      },
    };
  }, [documentNavigation, requestLeaveWithProceed]);

  const handleSaveAndLeave = React.useCallback(() => {
    if (!pendingLeave) return;
    if (performSave()) {
      pendingLeave.proceed();
      setPendingLeave(null);
    }
  }, [pendingLeave, performSave]);

  const handleDiscardAndLeave = React.useCallback(() => {
    if (!pendingLeave) return;
    pendingLeave.proceed();
    setPendingLeave(null);
  }, [pendingLeave]);

  const handleCancelLeave = React.useCallback(() => {
    setPendingLeave(null);
  }, []);

  const renderField = (
    originalRow: ValidatedExcelTransaction,
    uuid: string,
    key:
      | TransactionEditDrawerPerLineFieldKey
      | (typeof EDITABLE_ROW_KEYS)[number],
    options?: {
      applyToAllLines?: boolean;
      tableTextWrap?: boolean;
      /** Grid table cell — use Ant Tooltip instead of rc-select native title. */
      tableCellMode?: boolean;
    }
  ) => {
    const draftRow = draftMap[uuid] ?? {};
    const value: any = (draftRow as any)?.[key];

    const handleFieldChange = (
      fieldKey: keyof ValidatedExcelTransaction,
      fieldValue: any
    ) => {
      const validationFieldKey = (() => {
        if (fieldKey === 'contactId') return 'contactName' as const;
        if (fieldKey === 'taxRateId') return 'taxName' as const;
        if (
          Object.prototype.hasOwnProperty.call(
            TRANSACTION_EDIT_REQUIRED_FIELD_LABELS,
            fieldKey
          )
        ) {
          return fieldKey as TransactionEditRequiredFieldKey;
        }
        return null;
      })();
      if (validationFieldKey) {
        clearFieldValidationError(
          uuid,
          validationFieldKey,
          options?.applyToAllLines
        );
      }
      if (options?.applyToAllLines) {
        patchSharedField(fieldKey, fieldValue);
      } else {
        patchField(uuid, fieldKey, fieldValue);
      }
    };

    const formSelectProps = {
      width: '100%' as const,
      height: '33px',
      bordercolor: 'var(--color-border, #dbeae3)',
      bgcolor: 'var(--color-surface-raised, #FFFFFF)',
      radius: '0',
      // PWA: match field width + sentinel popup so menus are not clipped by
      // the transformed bottom-sheet (see usePwaDropdownSentinel).
      popupMatchSelectWidth: pwaMobileNav ? true : false,
      ...(pwaMobileNav
        ? {
            getPopupContainer: getPwaPopupContainer,
            placement: 'bottomLeft' as const,
          }
        : {
            styles: { popup: { root: { minWidth: 224 } } },
          }),
      classNames: {
        popup: {
          root: pwaMobileNav
            ? 'edit-drawer-select-dropdown scope-pwa-review-select-dropdown'
            : 'edit-drawer-select-dropdown',
        },
      },
      ...(options?.tableCellMode ? { title: null } : {}),
    };

    if (key === 'transactionDate' || key === 'dueDate') {
      const dateValue = value ? dayjs(value) : null;
      return (
        <DatePicker
          size="small"
          value={dateValue?.isValid() ? dateValue : null}
          format="DD/MM/YYYY"
          onChange={(date) =>
            handleFieldChange(key, date ? date.format('YYYY-MM-DD') : '')
          }
          style={{ width: '100%' }}
          getPopupContainer={
            pwaMobileNav ? getPwaPopupContainer : undefined
          }
        />
      );
    }

    if (key === 'currency') {
      return (
        <FormSelect
          showSearch
          value={value as string}
          options={currencyOptions}
          filterOption={(input: string, option: any) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={(val) => handleFieldChange('currency', val)}
          {...formSelectProps}
        />
      );
    }

    if (key === 'contactType') {
      return (
        <FormSelect
          value={value as string}
          options={contactTypeOptions}
          onChange={(val) => handleFieldChange('contactType', val)}
          {...formSelectProps}
        />
      );
    }

    if (key === 'sourceType') {
      return (
        <FormSelect
          showSearch
          value={value as string}
          options={sourceTypeOptions}
          filterOption={(input: string, option: any) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={(val) => handleFieldChange('sourceType', val)}
          {...formSelectProps}
        />
      );
    }

    if (key === 'taxInclusive') {
      const checked = value !== false;
      return (
        <Tooltip
          title={
            checked
              ? t('common.toggle-on-click-to-turn-off')
              : t('common.toggle-off-click-to-turn-on')
          }
        >
          <Switch
            size="small"
            checked={checked}
            className={checked ? 'switch-checked' : ''}
            aria-label={getEditDrawerLabel('taxInclusive', t)}
            onChange={(nextChecked) =>
              handleFieldChange('taxInclusive', nextChecked)
            }
          />
        </Tooltip>
      );
    }

    if (key === 'country') {
      return (
        <FormSelect
          showSearch
          value={value as string}
          options={countryOptions}
          filterOption={(input: string, option: any) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={(val) => handleFieldChange('country', val)}
          {...formSelectProps}
        />
      );
    }

    if (key === 'contactName') {
      const orig = originalRow as unknown as Record<string, unknown>;
      const rawValFromOriginal =
        (orig?.contactId as string) ?? (orig?.contactName as string);
      const rawVal = (value as string) ?? rawValFromOriginal;
      const rowSourceType =
        (draftRow as { sourceType?: string | null }).sourceType ??
        originalRow.sourceType;
      const rowContactId =
        (draftRow as { contactId?: string | null }).contactId ??
        originalRow.contactId;
      const contactOptionsForRow = filterContactOptionsForSourceType(
        contactOptions,
        contactTypeByContactId ?? new Map(),
        rowSourceType,
        providerName,
        rowContactId
      );

      const resolvedVal = (() => {
        if (!rawVal || contactOptionsForRow.length === 0) return rawVal;
        const trimmed = String(rawVal).trim();
        const byValue = contactOptionsForRow.find(
          (o) => String(o.value) === trimmed
        );
        const byLabel =
          byValue ??
          contactOptionsForRow.find((o) => String(o.label) === trimmed);
        return (byLabel?.value ?? rawVal) as string;
      })();

      return (
        <FormSelect
          showSearch
          value={resolvedVal as string}
          options={contactOptionsForRow}
          filterOption={(input: string, option: any) =>
            (option?.label ?? '')
              .toString()
              .toLowerCase()
              .includes((input ?? '').toLowerCase())
          }
          onChange={(val) => {
            const selected = contactOptionsForRow.find(
              (o) => o.value === (val as string)
            );
            handleFieldChange('contactId', val);
            handleFieldChange(
              'contactName',
              selected?.label ?? (draftRow as any)?.contactName
            );
          }}
          {...formSelectProps}
        />
      );
    }

    if (key === 'accountCode') {
      const orig = originalRow as unknown as Record<string, unknown>;
      const draft = draftRow as unknown as Record<string, unknown>;
      const rowFields = {
        accountId: (draft?.accountId ?? orig?.accountId) as string | null,
        accountCode: (draft?.accountCode ?? orig?.accountCode) as string | null,
        accountName: (draft?.accountName ?? orig?.accountName) as string | null,
      };
      const cellForResolve = draft?.accountId ?? draft?.accountCode ?? value;
      const {
        value: accountSelectValue,
        label: accountSelectLabel,
        options,
      } = resolveAccountSelectEditState({
        row: rowFields,
        cellVal: cellForResolve,
        isAccountIdProvider:
          isAccountIdProviderDrawer || coaAccountOptionsUseIds,
        accountNameByIdMap,
        accountNameOptions,
        accountNameOptionsGrouped,
      });
      const filterAccountOption = (input: string, option: any) => {
        const label = (option?.label ?? '').toString().toLowerCase();
        if (label.includes(input.toLowerCase())) return true;
        const opts = option?.options;
        if (Array.isArray(opts))
          return opts.some((o: any) =>
            (o?.label ?? '')
              .toString()
              .toLowerCase()
              .includes(input.toLowerCase())
          );
        return false;
      };
      return (
        <FormSelect
          showSearch
          labelInValue
          value={
            accountSelectValue && accountSelectLabel
              ? { value: accountSelectValue, label: accountSelectLabel }
              : undefined
          }
          options={options}
          filterOption={filterAccountOption}
          onChange={(item) => {
            const nextVal =
              item != null && typeof item === 'object' && 'value' in item
                ? String((item as { value: string }).value)
                : item != null
                ? String(item)
                : '';
            const accountFields = applyAccountSelectionToTransactionFields(
              nextVal,
              accountNameByIdMap,
              accountNameOptions,
              accountNameOptionsGrouped
            );
            (['accountId', 'accountCode', 'accountName'] as const).forEach(
              (fieldKey) => {
                handleFieldChange(
                  fieldKey as keyof ValidatedExcelTransaction,
                  accountFields[fieldKey]
                );
              }
            );
          }}
          {...formSelectProps}
        />
      );
    }

    if (key === 'itemId') {
      const orig = originalRow as unknown as Record<string, unknown>;
      const draft = draftRow as unknown as Record<string, unknown>;
      const rowFields = {
        itemId: (draft?.itemId ?? orig?.itemId) as string | null,
      };
      const cellForResolve = draft?.itemId ?? value;
      const {
        value: itemSelectValue,
        label: itemSelectLabel,
        options,
      } = resolveItemSelectEditState({
        row: rowFields,
        cellVal: cellForResolve,
        itemNameByIdMap,
        transactionItemOptions,
      });
      return (
        <FormSelect
          showSearch
          labelInValue
          value={
            itemSelectValue && itemSelectLabel
              ? { value: itemSelectValue, label: itemSelectLabel }
              : undefined
          }
          options={options}
          filterOption={(input: string, option: any) =>
            (option?.label ?? '')
              .toString()
              .toLowerCase()
              .includes((input ?? '').toLowerCase())
          }
          onChange={(item) => {
            const nextVal =
              item != null && typeof item === 'object' && 'value' in item
                ? String((item as { value: string }).value)
                : item != null
                ? String(item)
                : '';
            const itemFields = applyItemSelectionToTransactionFields(
              nextVal,
              itemNameByIdMap,
              transactionItemOptions
            );
            handleFieldChange(
              'itemId' as keyof ValidatedExcelTransaction,
              itemFields.itemId
            );
          }}
          {...formSelectProps}
        />
      );
    }

    if (key === 'trackingOption1Id' || key === 'trackingOption2Id') {
      const meta = trackingCategoryFieldMetaByKey.get(key);
      if (!meta) return null;
      const orig = originalRow as unknown as Record<string, unknown>;
      const draft = draftRow as unknown as Record<string, unknown>;
      const raw =
        draft[key] !== undefined && draft[key] !== null
          ? draft[key]
          : orig[key];
      const trimmed = raw == null || raw === '' ? '' : String(raw).trim();
      const currentId =
        trimmed === '' || trimmed === '-' || trimmed === '—' ? '' : trimmed;
      const nameField =
        key === 'trackingOption1Id'
          ? 'trackingOption1Name'
          : 'trackingOption2Name';
      const nameRaw =
        draft[nameField] !== undefined && draft[nameField] !== null
          ? draft[nameField]
          : orig[nameField];
      const optionName = nameRaw == null ? null : String(nameRaw);
      const options = trackingSelectOptionsForValue(
        trackingCategories,
        meta.dimensionIndex,
        currentId,
        optionName
      );
      const selectValue =
        currentId && options.some((option) => option.value === currentId)
          ? currentId
          : undefined;
      return (
        <FormSelect
          showSearch
          allowClear
          value={selectValue}
          options={options}
          filterOption={(input: string, option: any) =>
            String(option?.label ?? '')
              .toLowerCase()
              .includes((input ?? '').toLowerCase())
          }
          onChange={(val) => {
            const updates = applyTrackingOptionSelectionToTransactionFields(
              key,
              val === undefined || val === null || val === ''
                ? ''
                : String(val),
              trackingCategories
            );
            (
              Object.entries(updates) as [
                keyof ValidatedExcelTransaction,
                unknown
              ][]
            ).forEach(([fieldKey, fieldValue]) => {
              handleFieldChange(fieldKey, fieldValue);
            });
          }}
          {...formSelectProps}
        />
      );
    }

    if (key === 'taxName') {
      const orig = originalRow as ValidatedExcelTransaction;
      const draft = draftRow as ValidatedExcelTransaction;
      const {
        value: taxSelectValue,
        label: taxSelectLabel,
        options: taxSelectOptions,
      } = resolveTaxSelectEditState({
        row: orig,
        draftRow: draft,
        cellVal: value,
        taxNameByIdMap,
        taxNameOptions,
      });
      return (
        <FormSelect
          showSearch
          labelInValue
          value={
            taxSelectValue && taxSelectLabel
              ? { value: taxSelectValue, label: taxSelectLabel }
              : undefined
          }
          options={taxSelectOptions}
          filterOption={(input: string, option: any) =>
            (option?.label ?? '')
              .toString()
              .toLowerCase()
              .includes((input ?? '').toLowerCase())
          }
          onChange={(item) => {
            const nextVal =
              item != null && typeof item === 'object' && 'value' in item
                ? String((item as { value: string }).value)
                : item != null
                ? String(item)
                : '';
            const nextDraft = syncTaxFieldsFromRateId(
              {
                ...(draftRow as ValidatedExcelTransaction),
                taxRateId: nextVal || null,
              },
              taxRateByIdMap
            );
            handleFieldChange(
              'taxRateId' as keyof ValidatedExcelTransaction,
              nextDraft.taxRateId ?? null
            );
            if (nextDraft.taxName != null) {
              handleFieldChange(
                'taxName' as keyof ValidatedExcelTransaction,
                nextDraft.taxName
              );
            }
            if (nextDraft.taxRate != null) {
              handleFieldChange(
                'taxRate' as keyof ValidatedExcelTransaction,
                nextDraft.taxRate
              );
            }
          }}
          {...formSelectProps}
        />
      );
    }

    if (key === 'taxRate') {
      const displayVal = value;
      const display =
        displayVal != null && displayVal !== ''
          ? `${String(displayVal)}%`
          : '-';
      return (
        <Input
          size="small"
          value={display}
          readOnly
          style={{
            backgroundColor: 'var(--color-review-readonly-bg, #f5f5f5)',
            cursor: 'default',
          }}
        />
      );
    }

    if (String(key) === 'uploadDate') {
      const display =
        value != null && value !== ''
          ? formatTransactionTableDate(
              value as string | number | Date,
              i18n.language
            )
          : '-';
      return (
        <Input
          size="small"
          value={display}
          readOnly
          style={{
            backgroundColor: 'var(--color-review-readonly-bg, #f5f5f5)',
            cursor: 'default',
          }}
        />
      );
    }

    if (
      isEntityTransactionColumnReadOnly(
        key,
        deriveGrossNetFromQuantityUnitPrice
      )
    ) {
      const { gross, net } = resolveCalculatedGrossAndNet(draftRow);
      const amount = key === 'gross' ? gross : key === 'net' ? net : value;
      const display = formatTransactionAmountDisplay(amount);
      return (
        <Input
          size="small"
          value={display}
          readOnly
          style={{
            backgroundColor: 'var(--color-review-readonly-bg, #f5f5f5)',
            cursor: 'default',
          }}
        />
      );
    }

    if (NUMERIC_COLUMN_KEYS.has(key)) {
      const displayValue = formatTransactionNumericInputValue(
        key,
        value,
        TRANSACTION_AMOUNT_FIELD_KEYS
      );
      return (
        <Input
          size="small"
          value={displayValue}
          onFocus={
            key === 'tax' && deriveGrossNetFromQuantityUnitPrice
              ? handleReceiptTaxFieldFocus
              : undefined
          }
          onChange={(e) => {
            const raw = e.target.value;
            const sanitised =
              key === 'quantity'
                ? sanitiseQuantityInput(raw)
                : raw
                    .replace(/,/g, '.')
                    .replace(/[^0-9.\-]/g, '')
                    .replace(/(?!^)-/g, '')
                    .replace(/(\..*)\./g, '$1');
            handleFieldChange(key, sanitised);
          }}
          onBlur={() => {
            setDraftMap((prev) => {
              const rowDraft = { ...(prev[uuid] ?? {}) };
              const rawNumeric = (rowDraft as any)[key];
              let nextRow: Partial<ValidatedExcelTransaction>;
              if (
                rawNumeric == null ||
                rawNumeric === '' ||
                rawNumeric === '-'
              ) {
                nextRow = {
                  ...rowDraft,
                  [key]: OPTIONAL_NUMERIC_COLUMN_KEYS.has(key) ? null : 0,
                };
              } else {
                const parsed = parseLocaleNumber(rawNumeric);
                nextRow = {
                  ...rowDraft,
                  [key]:
                    key === 'quantity'
                      ? toApiQuantity(parsed)
                      : toApiNumber(parsed),
                };
              }
              if (
                deriveGrossNetFromQuantityUnitPrice &&
                (key === 'quantity' ||
                  key === 'unitPrice' ||
                  key === 'discountAmount' ||
                  key === 'tax')
              ) {
                nextRow = applyCalculatedGrossAndNet(nextRow);
              }
              return {
                ...prev,
                [uuid]: nextRow,
              };
            });
          }}
        />
      );
    }

    if (key === 'description' && options?.tableTextWrap) {
      return (
        <Input.TextArea
          size="small"
          autoSize={{ minRows: 1, maxRows: 4 }}
          value={value as any}
          onChange={(e) => handleFieldChange(key, e.target.value)}
          style={{ resize: 'none' }}
        />
      );
    }

    return (
      <Input
        size="small"
        value={value as any}
        onChange={(e) => handleFieldChange(key, e.target.value)}
      />
    );
  };

  // Match table columns: figures end with Qty → Unit Price → Discount → Tax → Net → Total.
  // Table excludes: taxRate, accountType, contactType. For account providers also: taxRateId, accountId (Tax Name column edits taxRateId; accountCode edits accountId).
  const keysList = React.useMemo(() => {
    const drawerFieldOrder: string[] = [
      'uploadDate',
      'transactionDate',
      'dueDate',
      'currency',
      'exchangeRate',
      'itemId',
      'sourceType',
      'transactionNumber',
      'description',
      'taxName',
      'accountCode',
      'reference',
      'contactName',
      'taxInclusive',
      'quantity',
      'unitPrice',
      'discountAmount',
      'tax',
      'net',
      'gross',
    ];
    const excludedForAccountProvider = new Set(['taxRateId', 'accountId']);
    return (
      isAccountIdProviderDrawer
        ? drawerFieldOrder.filter((k) => !excludedForAccountProvider.has(k))
        : drawerFieldOrder
    )
      .filter((k) => !DRAWER_EXCLUDED_FIELD_KEYS.has(k))
      .filter((k) => showTransactionItemColumns || k !== 'itemId')
      .filter(
        (k) =>
          deriveGrossNetFromQuantityUnitPrice ||
          (k !== 'quantity' && k !== 'unitPrice' && k !== 'taxInclusive')
      )
      .filter(
        (k) =>
          EDITABLE_ROW_KEYS.includes(k as (typeof EDITABLE_ROW_KEYS)[number]) ||
          READ_ONLY_TRANSACTION_TAB_COLUMN_KEYS.has(k) ||
          isEntityTransactionColumnReadOnly(
            k,
            deriveGrossNetFromQuantityUnitPrice
          )
      );
  }, [
    isAccountIdProviderDrawer,
    showTransactionItemColumns,
    deriveGrossNetFromQuantityUnitPrice,
  ]);

  const normalizedColumnCount =
    normalizeTransactionEditDrawerColumnCount(columnCount);
  /** PWA review: two side-by-side field columns (desktop keeps user preference). */
  const layoutColumnCount = pwaMobileNav ? 2 : normalizedColumnCount;

  React.useEffect(() => {
    if (pwaMobileNav && lineViewMode === 'table') {
      setLineViewMode('form');
    }
  }, [pwaMobileNav, lineViewMode]);

  const rowsWithIds = React.useMemo(
    () => visibleTransactions.filter((tx) => tx.uuid),
    [visibleTransactions]
  );
  const canDeleteLines =
    Boolean(onDeleteLine) && rowsWithIds.length > 1 && !isSaving;

  /** Multi-line document review: shared header + per-line amounts/accounts (not single Excel row mode). */
  const useGroupedDocumentFieldsLayout =
    rowsWithIds.length > 1 && documentNavigation?.mode !== 'transaction';

  /** Single Excel / lone transaction: same field split as multi-line, without changing multi-line UI. */
  const useSingleLineSplitLayout =
    rowsWithIds.length === 1 && !useGroupedDocumentFieldsLayout;

  const useFieldSplitLayout =
    useGroupedDocumentFieldsLayout || useSingleLineSplitLayout;

  React.useEffect(() => {
    if (!useFieldSplitLayout && lineViewMode === 'table') {
      setLineViewMode('form');
    }
  }, [useFieldSplitLayout, lineViewMode]);

  const sharedFieldKeySet = React.useMemo(
    () => new Set<string>(DOCUMENT_GROUP_SHARED_DRAWER_KEYS),
    []
  );

  const sharedKeysList = React.useMemo(
    () =>
      keysList.filter((k) =>
        sharedFieldKeySet.has(k)
      ) as DocumentGroupSharedDrawerKey[],
    [keysList, sharedFieldKeySet]
  );

  const perLineKeysList = React.useMemo(() => {
    const allowed = new Set(
      keysList.filter((k) => !sharedFieldKeySet.has(k as string))
    );
    return buildDrawerPerLineFieldOrder(showTrackingCategoryColumns, allowed);
  }, [keysList, sharedFieldKeySet, showTrackingCategoryColumns]);

  type DrawerLayoutFieldKey =
    | DocumentGroupSharedDrawerKey
    | TransactionEditDrawerPerLineFieldKey
    | (typeof EDITABLE_ROW_KEYS)[number];

  const chunkKeysIntoColumns = React.useCallback(
    (list: readonly DrawerLayoutFieldKey[], rawColumnCount: number) => {
      const normalizedList = (list ?? []).filter(
        Boolean
      ) as DrawerLayoutFieldKey[];
      const columnCount = Math.max(
        1,
        Math.min(rawColumnCount, normalizedList.length || 1)
      );
      const cols: DrawerLayoutFieldKey[][] = Array.from(
        { length: columnCount },
        () => []
      );
      // Round-robin distribution keeps all columns non-empty when possible
      // (e.g. 9 fields across 4 cols => 3/2/2/2 instead of 3/3/3/0).
      normalizedList.forEach((key, idx) => {
        cols[idx % columnCount].push(key);
      });
      return cols;
    },
    []
  );

  const columnKeyChunks = React.useMemo(() => {
    const list = (
      useFieldSplitLayout ? perLineKeysList : keysList
    ) as DrawerLayoutFieldKey[];
    return chunkKeysIntoColumns(list, layoutColumnCount);
  }, [
    keysList,
    perLineKeysList,
    layoutColumnCount,
    useFieldSplitLayout,
    chunkKeysIntoColumns,
  ]);

  const sharedColumnKeyChunks = React.useMemo(() => {
    if (!useFieldSplitLayout || sharedKeysList.length === 0) {
      return [];
    }
    return chunkKeysIntoColumns(sharedKeysList, layoutColumnCount);
  }, [
    sharedKeysList,
    useFieldSplitLayout,
    layoutColumnCount,
    chunkKeysIntoColumns,
  ]);

  /** PWA detail mockup: Document vs Currency sections. */
  const pwaDocumentFieldKeys = React.useMemo(() => {
    const documentOrder: DocumentGroupSharedDrawerKey[] = [
      'transactionDate',
      'dueDate',
      'transactionNumber',
      'reference',
      'sourceType',
      'contactName',
      'taxInclusive',
    ];
    const set = new Set(sharedKeysList);
    return documentOrder.filter((k) => set.has(k));
  }, [sharedKeysList]);

  const pwaCurrencyFieldKeys = React.useMemo(() => {
    const currencyOrder: DocumentGroupSharedDrawerKey[] = [
      'currency',
      'exchangeRate',
      'uploadDate',
    ];
    const set = new Set(sharedKeysList);
    return currencyOrder.filter((k) => set.has(k));
  }, [sharedKeysList]);

  const pwaDocumentFieldChunks = React.useMemo(
    () =>
      pwaMobileNav && pwaDocumentFieldKeys.length > 0
        ? chunkKeysIntoColumns(pwaDocumentFieldKeys, layoutColumnCount)
        : [],
    [
      pwaMobileNav,
      pwaDocumentFieldKeys,
      layoutColumnCount,
      chunkKeysIntoColumns,
    ]
  );

  const pwaCurrencyFieldChunks = React.useMemo(
    () =>
      pwaMobileNav && pwaCurrencyFieldKeys.length > 0
        ? chunkKeysIntoColumns(pwaCurrencyFieldKeys, layoutColumnCount)
        : [],
    [
      pwaMobileNav,
      pwaCurrencyFieldKeys,
      layoutColumnCount,
      chunkKeysIntoColumns,
    ]
  );

  const anchorRow = rowsWithIds[0];
  const anchorUuid = anchorRow?.uuid ? String(anchorRow.uuid) : '';

  const pwaSummaryMeta = React.useMemo(() => {
    if (!pwaMobileNav || !anchorRow || !anchorUuid) return null;
    const draftRow = draftMap[anchorUuid] ?? {};
    const sourceTypeValue = String(
      draftRow.sourceType ?? anchorRow.sourceType ?? ''
    ).trim();
    const sourceTypeLabel =
      sourceTypeOptions.find((o) => String(o.value) === sourceTypeValue)
        ?.label ?? sourceTypeValue;
    const txnNumber = String(
      draftRow.transactionNumber ?? anchorRow.transactionNumber ?? ''
    ).trim();
    const titleParts = [sourceTypeLabel, txnNumber].filter(Boolean);
    const title =
      titleParts.length > 0
        ? titleParts.join(' · ')
        : t('organisation.document-details', {
            defaultValue: 'Document details',
          });
    const providerLabel = String(providerName ?? '').trim();
    const postsMeta =
      providerLabel.length > 0
        ? t('organisation.pwa-posts-to-provider', {
            defaultValue: 'Posts to {{provider}}',
            provider: providerLabel,
          })
        : null;
    const reviewStatus = anchorRow.reviewStatus;
    const isReviewed = reviewStatus === ReviewStatus.REVIEWED;
    return { title, postsMeta, isReviewed };
  }, [
    pwaMobileNav,
    anchorRow,
    anchorUuid,
    draftMap,
    sourceTypeOptions,
    providerName,
    t,
  ]);

  /** Normal / Compact + column segmented pair (margins applied only when placed above transaction lines). */
  const densityColumnSegmentClustersOnly = React.useMemo(
    () => (
      <TransactionReviewDrawerSegmentedWrap>
        <Segmented<string>
          size="small"
          value={density}
          onChange={(v) => setDensity(v === 'compact' ? 'compact' : 'normal')}
          options={[
            {
              label: t('organisation.transaction-edit-layout-normal'),
              value: 'normal',
            },
            {
              label: t('organisation.transaction-edit-layout-compact'),
              value: 'compact',
            },
          ]}
        />
        <Segmented<string | number>
          size="small"
          value={
            useFieldSplitLayout && lineViewMode === 'table'
              ? 'table'
              : normalizedColumnCount
          }
          disabled={isSaving}
          onChange={(v) => {
            if (v === 'table') {
              setLineViewMode('table');
              return;
            }
            setLineViewMode('form');
            setColumnCount(
              normalizeTransactionEditDrawerColumnCount(Number(v))
            );
          }}
          options={[
            { label: '3 columns', value: 3 },
            { label: '4 columns', value: 4 },
            { label: '5 columns', value: 5 },
            { label: '6 columns', value: 6 },
            ...(useFieldSplitLayout
              ? [
                  {
                    label: t('organisation.transaction-edit-layout-grid'),
                    value: 'table',
                  },
                ]
              : []),
          ]}
        />
        {showEditInDrawerSetting ? (
          <TransactionEditModeSegmented
            value="drawer"
            disabled={isSaving}
            onChange={handleEditModeChangeRequest}
          />
        ) : null}
      </TransactionReviewDrawerSegmentedWrap>
    ),
    [
      density,
      normalizedColumnCount,
      lineViewMode,
      useFieldSplitLayout,
      showEditInDrawerSetting,
      handleEditModeChangeRequest,
      isSaving,
    ]
  );

  /** Integrated shell header: document nav, Save, Cancel (segment controls sit above Line 1 in the form body). */
  const integratedDrawerChromeToolbar = React.useMemo(() => {
    const actionButtons = (
      <>
        <ScopeButton
          type="primary"
          onClick={handleSubmit}
          height="32px"
          width={pwaMobileNav ? '100%' : '140px'}
          fontSize="12px"
          style={{ borderRadius: 50 }}
          disabled={isSaving}
        >
          {isSaving ? t('common.saving') : t('common.save')}
        </ScopeButton>
        {showRescan ? (
          pwaMobileNav ? (
            <span className="txn-review-drawer-chrome__btn-wrap">
              <ScopeButton
                type="default"
                onClick={onRescan}
                height="32px"
                width="100%"
                fontSize="11px"
                style={{ borderRadius: 50 }}
                disabled={isSaving}
              >
                {resolvedRescanLabel}
              </ScopeButton>
            </span>
          ) : (
            <Tooltip title={rescanTooltip} placement="bottom">
              <span className="txn-review-drawer-chrome__btn-wrap">
                <ScopeButton
                  type="default"
                  onClick={onRescan}
                  height="32px"
                  width="96px"
                  fontSize="11px"
                  style={{ borderRadius: 50 }}
                  disabled={isSaving}
                >
                  {resolvedRescanLabel}
                </ScopeButton>
              </span>
            </Tooltip>
          )
        ) : null}
        <ScopeButton
          type="default"
          onClick={handleRequestClose}
          height="32px"
          width={pwaMobileNav ? '100%' : '96px'}
          fontSize="11px"
          style={{ borderRadius: 50 }}
          disabled={isSaving}
        >
          {t('common.close')}
        </ScopeButton>
      </>
    );

    if (pwaMobileNav) {
      const contactName = String(
        (anchorRow as { contactName?: string | null } | undefined)
          ?.contactName ?? ''
      ).trim();
      const title = contactName || (
        <span style={{ color: 'var(--gt-warn, #c48a00)' }}>
          {t('organisation.pwa-review-supplier-missing', {
            defaultValue: 'Supplier not found',
          })}
        </span>
      );
      const idx = documentNavigation?.currentIndex;
      const total = documentNavigation?.totalCount;
      const uploadedRaw = (anchorRow as { uploadDate?: number | string })
        ?.uploadDate;
      const uploadedLabel = uploadedRaw
        ? formatTransactionTableDate(uploadedRaw, i18n.language)
        : '';
      const positionLabel =
        idx != null && total != null && total > 0
          ? t('organisation.pwa-review-document-position', {
              defaultValue: 'Document {{current}} of {{total}}',
              current: idx,
              total,
            })
          : t('organisation.review-document-drawer-title', {
              defaultValue: 'Review document',
            });
      const subtitle = uploadedLabel
        ? `${positionLabel} · ${uploadedLabel}`
        : positionLabel;

      const moreItems: MenuProps['items'] = [
        ...(showRescan
          ? [
              {
                key: 'rescan',
                label: resolvedRescanLabel,
                disabled: isSaving,
                onClick: () => onRescan?.(),
              },
            ]
          : []),
        {
          key: 'close',
          label: t('common.close'),
          disabled: isSaving,
          onClick: () => handleRequestClose(),
        },
      ];

      return (
        <div className="txn-review-drawer-chrome txn-review-drawer-chrome--pwa txn-review-drawer-chrome--pwa-detail">
          <div className="txn-review-drawer-chrome__detail-top">
            <button
              type="button"
              className="pwa-iconbtn subtle"
              aria-label={t('common.back', { defaultValue: 'Back' })}
              onClick={handleRequestClose}
              disabled={isSaving}
            >
              <PwaIcon name="back" />
            </button>
            <div className="txn-review-drawer-chrome__detail-title">
              <strong>{title}</strong>
              <span>{subtitle}</span>
            </div>
            {onActivity ? (
              <button
                type="button"
                className="pwa-iconbtn subtle"
                aria-label={t('organisation.reconciliation-activity', {
                  defaultValue: 'Activity',
                })}
                onClick={onActivity}
              >
                <PwaIcon name="history" />
              </button>
            ) : null}
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              menu={{ items: moreItems }}
            >
              <button
                type="button"
                className="pwa-iconbtn subtle"
                aria-label={t('common.more-actions')}
              >
                <PwaIcon name="dots" />
              </button>
            </Dropdown>
          </div>
        </div>
      );
    }

    return (
      <div className="txn-review-drawer-chrome">
        {guardedDocumentNavigation ? (
          <Space className="txn-review-drawer-chrome__nav" size={6}>
            <Tooltip title={
              guardedDocumentNavigation.mode === 'transaction'
                ? t('organisation.previous-transaction')
                : t('organisation.previous-document')
            }>
              <span>
                <IconButton
                  icon={<LeftOutlined />}
                  onClick={guardedDocumentNavigation.onNavigateToPrevious}
                  disabled={
                    isSaving || !guardedDocumentNavigation.hasPrevious
                  }
                  aria-label={
                    guardedDocumentNavigation.mode === 'transaction'
                      ? t('organisation.previous-transaction')
                      : t('organisation.previous-document')
                  }
                />
              </span>
            </Tooltip>
            <Tooltip title={
              guardedDocumentNavigation.mode === 'transaction'
                ? t('organisation.next-transaction')
                : t('organisation.next-document')
            }>
              <span>
                <IconButton
                  icon={<RightOutlined />}
                  onClick={guardedDocumentNavigation.onNavigateToNext}
                  disabled={isSaving || !guardedDocumentNavigation.hasNext}
                  aria-label={
                    guardedDocumentNavigation.mode === 'transaction'
                      ? t('organisation.next-transaction')
                      : t('organisation.next-document')
                  }
                />
              </span>
            </Tooltip>
          </Space>
        ) : null}
        <div className="txn-review-drawer-chrome__actions">{actionButtons}</div>
      </div>
    );
  }, [
      handleSubmit,
      handleRequestClose,
      guardedDocumentNavigation,
      documentNavigation?.currentIndex,
      documentNavigation?.totalCount,
      isSaving,
      showRescan,
      onRescan,
      resolvedRescanLabel,
      rescanTooltip,
      pwaMobileNav,
      onActivity,
      anchorRow,
      i18n.language,
      t,
    ]
  );

  const pwaPostAsOptions = React.useMemo(() => {
    const providerKey = providerName?.toLowerCase() ?? '';
    if (providerKey === Provider.XERO.toLowerCase()) {
      return [
        { id: 'DRAFT' as const, label: t('organisation.file-post-as-draft') },
        {
          id: 'SUBMITTED' as const,
          label: t('organisation.file-post-as-awaiting-approval'),
        },
        {
          id: 'AUTHORISED' as const,
          label: t('organisation.file-post-as-authorised'),
        },
      ];
    }
    if (providerKey === Provider.ZOHO.toLowerCase()) {
      return [
        { id: 'DRAFT' as const, label: t('organisation.file-post-as-draft') },
        {
          id: 'AUTHORISED' as const,
          label: t('organisation.file-post-as-authorised'),
        },
      ];
    }
    return [];
  }, [providerName, t]);

  const showPwaSaveAndPost = Boolean(
    pwaMobileNav &&
      showRescan &&
      pwaSummaryMeta &&
      !pwaSummaryMeta.isReviewed &&
      (pwaPostAsOptions.length > 0 ||
        (providerName?.toLowerCase() ?? '') === Provider.QUICKBOOKS)
  );

  const confirmSaveAndPost = React.useCallback(
    (status?: ProviderPostStatus) => {
      setPostSheetOpen(false);
      performSave(pwaPostAsOptions.length > 0 ? { status } : {});
    },
    [performSave, pwaPostAsOptions.length]
  );

  const pwaActionbar = pwaMobileNav ? (
    <>
      {showPwaSaveAndPost ? (
        <button
          type="button"
          className="pwa-btn tonal post"
          onClick={() => {
            if (pwaPostAsOptions.length === 0) {
              confirmSaveAndPost(undefined);
              return;
            }
            setPostAs(pwaPostAsOptions[0].id);
            setPostSheetOpen(true);
          }}
          disabled={isSaving}
        >
          <PwaIcon name="upload" size={19} />
          {t('organisation.pwa-save-and-post', { defaultValue: 'Save and Post' })}
        </button>
      ) : null}
      {showRescan ? (
        <button
          type="button"
          className="pwa-btn secondary"
          onClick={onRescan}
          disabled={isSaving}
        >
          <PwaIcon name="refresh" size={19} />
          {resolvedRescanLabel}
        </button>
      ) : null}
      <button
        type="button"
        className="pwa-btn primary"
        onClick={handleSubmit}
        disabled={isSaving}
      >
        <PwaIcon name="check" size={20} />
        {isSaving ? t('common.saving') : t('common.save')}
      </button>
      {postSheetOpen ? (
        <PwaSheet
          className="pwa-save-and-post-sheet"
          title={t('organisation.pwa-save-and-post', {
            defaultValue: 'Save and Post',
          })}
          sub={t('organisation.pwa-save-and-post-prompt', {
            defaultValue: 'How should this be posted?',
          })}
          onClose={() => setPostSheetOpen(false)}
          footer={
            <button
              type="button"
              className="pwa-btn primary"
              disabled={isSaving}
              onClick={() => confirmSaveAndPost(postAs)}
            >
              <PwaIcon name="check" size={20} />
              {pwaPostAsOptions.find((option) => option.id === postAs)?.label}
            </button>
          }
        >
          <div className="pwa-list" role="radiogroup" aria-label={t('organisation.pwa-save-and-post', { defaultValue: 'Save and Post' })}>
            {pwaPostAsOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                className="pwa-row"
                role="radio"
                aria-checked={postAs === option.id}
                onClick={() => setPostAs(option.id)}
              >
                <span className="pwa-row-body">
                  <span className="pwa-row-title">{option.label}</span>
                </span>
                {postAs === option.id ? <PwaIcon name="check" size={18} /> : null}
              </button>
            ))}
          </div>
        </PwaSheet>
      ) : null}
    </>
  ) : null;

  const inlineFallbackFullToolbar = React.useMemo(
    () => (
      <div
        className="txn-review-drawer-toolbar-row"
        style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px 16px',
          minWidth: 0,
        }}
      >
        <div style={{ flex: '0 1 auto', minWidth: 0 }}>
          {densityColumnSegmentClustersOnly}
        </div>
        <Space
          size={12}
          wrap
          align="center"
          style={{ flexShrink: 0, marginLeft: 'auto' }}
        >
          {guardedDocumentNavigation ? (
            <Space className="txn-review-drawer-chrome__nav" size={6}>
              <Tooltip
                title={
                  guardedDocumentNavigation.mode === 'transaction'
                    ? t('organisation.previous-transaction')
                    : t('organisation.previous-document')
                }
              >
                <span>
                  <IconButton
                    icon={<LeftOutlined />}
                    onClick={guardedDocumentNavigation.onNavigateToPrevious}
                    disabled={
                      isSaving || !guardedDocumentNavigation.hasPrevious
                    }
                    aria-label={
                      guardedDocumentNavigation.mode === 'transaction'
                        ? t('organisation.previous-transaction')
                        : t('organisation.previous-document')
                    }
                  />
                </span>
              </Tooltip>
              <Tooltip
                title={
                  guardedDocumentNavigation.mode === 'transaction'
                    ? t('organisation.next-transaction')
                    : t('organisation.next-document')
                }
              >
                <span>
                  <IconButton
                    icon={<RightOutlined />}
                    onClick={guardedDocumentNavigation.onNavigateToNext}
                    disabled={isSaving || !guardedDocumentNavigation.hasNext}
                    aria-label={
                      guardedDocumentNavigation.mode === 'transaction'
                        ? t('organisation.next-transaction')
                        : t('organisation.next-document')
                    }
                  />
                </span>
              </Tooltip>
            </Space>
          ) : null}
          <div className="txn-review-drawer-chrome__actions">
            <ScopeButton
              type="primary"
              onClick={handleSubmit}
              height="32px"
              width="140px"
              fontSize="12px"
              style={{ borderRadius: 50 }}
              disabled={isSaving}
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </ScopeButton>
            {showRescan ? (
              <Tooltip title={rescanTooltip} placement="bottom">
                <span className="txn-review-drawer-chrome__btn-wrap">
                  <ScopeButton
                    type="default"
                    onClick={onRescan}
                    height="32px"
                    width="96px"
                    fontSize="11px"
                    style={{ borderRadius: 50 }}
                    disabled={isSaving}
                  >
                    {resolvedRescanLabel}
                  </ScopeButton>
                </span>
              </Tooltip>
            ) : null}
            <ScopeButton
              type="default"
              onClick={handleRequestClose}
              height="32px"
              width="96px"
              fontSize="11px"
              style={{ borderRadius: 50 }}
              disabled={isSaving}
            >
              {t('common.close')}
            </ScopeButton>
          </div>
        </Space>
      </div>
    ),
    [
      densityColumnSegmentClustersOnly,
      handleSubmit,
      handleRequestClose,
      guardedDocumentNavigation,
      isSaving,
      showRescan,
      onRescan,
      resolvedRescanLabel,
      rescanTooltip,
    ]
  );

  // PWA renders chrome via portal into the sheet body; avoid also mounting it
  // into AppDrawer's integrated header (would duplicate the toolbar).
  useIntegratedDrawerToolbar(
    integratedDrawerHeader && !pwaMobileNav,
    integratedDrawerHeader && !pwaMobileNav ? integratedDrawerChromeToolbar : null
  );

  const tooltipTitleParams = React.useMemo(
    () => ({
      isAccountIdProvider: isAccountIdProviderDrawer,
      coaAccountOptionsUseIds,
      sourceTypeOptions,
      contactOptions,
      taxNameOptions,
      accountNameOptions,
      accountNameOptionsGrouped,
      itemNameByIdMap,
      transactionItemOptions,
      organisationTaxRates,
    }),
    [
      isAccountIdProviderDrawer,
      coaAccountOptionsUseIds,
      sourceTypeOptions,
      contactOptions,
      taxNameOptions,
      accountNameOptions,
      accountNameOptionsGrouped,
      itemNameByIdMap,
      transactionItemOptions,
      organisationTaxRates,
    ]
  );

  const interpretationRuleFieldOptionsForPreview =
    React.useMemo((): InterpretationRuleFieldOptions => {
      return {
        fieldOptions: [],
        accountOptions: accountNameOptions,
        taxOptions: taxNameOptions,
        contactNameOptions: contactOptions.map((option) => ({
          label: option.label,
          value: String(option.value),
          name: option.name,
        })),
        itemOptions: transactionItemOptions,
        sourceTypeOptions,
      };
    }, [
      accountNameOptions,
      contactOptions,
      sourceTypeOptions,
      taxNameOptions,
      transactionItemOptions,
    ]);

  const renderCreateRuleLink = React.useCallback(
    (
      fieldKey: string,
      originalRow: ValidatedExcelTransaction,
      rowUuid: string,
      options?: { iconOnly?: boolean }
    ) => {
      if (
        !onCreateInterpretationRule ||
        !CREATE_RULE_FIELD_KEYS.has(fieldKey)
      ) {
        return null;
      }

      const reviewSeed = reviewSeedById.get(rowUuid) ?? originalRow;
      const draftRow = draftMap[rowUuid] ?? {};

      return (
        <CreateCaptureRuleIconLink
          fieldKey={fieldKey as InterpretationRuleField}
          reviewSeed={reviewSeed}
          draftRow={draftRow}
          fieldOptions={interpretationRuleFieldOptionsForPreview}
          iconOnly={options?.iconOnly}
          onCreateRule={(draft) => onCreateInterpretationRule?.(draft)}
        />
      );
    },
    [
      onCreateInterpretationRule,
      reviewSeedById,
      draftMap,
      interpretationRuleFieldOptionsForPreview,
    ]
  );

  const renderColumn = (
    originalRow: ValidatedExcelTransaction,
    rowUuid: string,
    keys: readonly (
      | TransactionEditDrawerPerLineFieldKey
      | DocumentGroupSharedDrawerKey
      | (typeof EDITABLE_ROW_KEYS)[number]
    )[],
    options?: { applyToAllLines?: boolean; equalColumnLayout?: boolean }
  ) => {
    const applyToAllLines = options?.applyToAllLines === true;
    const equalColumnLayout = options?.equalColumnLayout === true;
    return (
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: isCompact ? 2 : 4,
        }}
      >
        {keys.map((fieldKey) => {
          const trackingMeta =
            fieldKey === 'trackingOption1Id' || fieldKey === 'trackingOption2Id'
              ? trackingCategoryFieldMetaByKey.get(fieldKey)
              : undefined;
          const tooltipKey = EDIT_DRAWER_TOOLTIP_KEY[fieldKey];
          const labelText =
            trackingMeta?.label ??
            (tooltipKey != null
              ? getMapImportFieldLabel(tooltipKey, t)
              : getEditDrawerLabel(fieldKey, t));
          const isOptional = editDrawerOptionalKeys.has(fieldKey);
          const fieldTooltipTitle = resolveEditDrawerFieldTooltipTitle(
            t,
            fieldKey,
            isOptional
          );
          const showCreateRuleLink =
            Boolean(onCreateInterpretationRule) &&
            CREATE_RULE_FIELD_KEYS.has(fieldKey);
          return (
            <EditDrawerFieldStack
              key={fieldKey}
              $compact={isCompact}
              $equalColumn={equalColumnLayout}
            >
              <EditDrawerStackedLabel
                $compact={isCompact}
                $equalColumn={equalColumnLayout}
              >
                <EditDrawerLabelWithTooltip $truncate={equalColumnLayout}>
                  <span>{labelText}</span>
                  {fieldTooltipTitle != null && (
                    <Tooltip title={fieldTooltipTitle}>
                      <EditDrawerQuestionMarkWrapper>
                        <IconQuestionMark
                          width={isCompact ? 14 : 15}
                          height={isCompact ? 14 : 15}
                        />
                      </EditDrawerQuestionMarkWrapper>
                    </Tooltip>
                  )}
                </EditDrawerLabelWithTooltip>
                {showCreateRuleLink ? (
                  <EditDrawerLabelTrail>
                    {renderCreateRuleLink(fieldKey, originalRow, rowUuid, {
                      iconOnly: true,
                    })}
                  </EditDrawerLabelTrail>
                ) : null}
              </EditDrawerStackedLabel>
              <EditDrawerControlWrap
                $fullWidth
                $equalColumn={equalColumnLayout}
                $invalid={
                  Object.prototype.hasOwnProperty.call(
                    TRANSACTION_EDIT_REQUIRED_FIELD_LABELS,
                    fieldKey
                  ) &&
                  isDrawerFieldInvalid(
                    rowUuid,
                    fieldKey as TransactionEditRequiredFieldKey,
                    applyToAllLines
                  )
                }
                $capControlWidthPx={undefined}
              >
                {(() => {
                  const draftRow = draftMap[rowUuid] ?? {};
                  const hoverTitle =
                    equalColumnLayout &&
                    fieldKey !== 'trackingOption1Id' &&
                    fieldKey !== 'trackingOption2Id'
                      ? resolveDrawerFieldTooltipTitle({
                          fieldKey:
                            fieldKey as (typeof EDITABLE_ROW_KEYS)[number],
                          originalRow,
                          draftRow,
                          ...tooltipTitleParams,
                        })
                      : '';
                  const control = renderField(
                    originalRow,
                    rowUuid,
                    fieldKey as TransactionEditDrawerPerLineFieldKey,
                    {
                      applyToAllLines,
                    }
                  );
                  if (!hoverTitle.trim()) {
                    return control;
                  }
                  return (
                    <Tooltip title={hoverTitle}>
                      <EditDrawerControlTooltipTarget>
                        {control}
                      </EditDrawerControlTooltipTarget>
                    </Tooltip>
                  );
                })()}
              </EditDrawerControlWrap>
            </EditDrawerFieldStack>
          );
        })}
      </ul>
    );
  };

  const useLineFieldsEqualColumns = useFieldSplitLayout;

  const documentAmountTotals = React.useMemo(
    () => sumDocumentDrawerAmountTotals(rowsWithIds, draftMap),
    [rowsWithIds, draftMap]
  );

  const documentTotalsCurrencyCode = React.useMemo(
    () => resolveTransactionTotalsCurrencyCode(rowsWithIds, draftMap),
    [rowsWithIds, draftMap]
  );

  const formatDocumentTotalAmount = React.useCallback(
    (amount: number) =>
      formatFormattedAmountWithCurrency(
        documentTotalsCurrencyCode,
        locale,
        formatTransactionAmountDisplay(amount)
      ),
    [documentTotalsCurrencyCode, locale]
  );

  const resolveDrawerTableFieldLabel = React.useCallback(
    (fieldKey: string) => {
      const overrideKey = DRAWER_TABLE_HEADER_LABEL_I18N_KEYS[fieldKey];
      if (overrideKey) return t(overrideKey);

      const trackingMeta =
        fieldKey === 'trackingOption1Id' || fieldKey === 'trackingOption2Id'
          ? trackingCategoryFieldMetaByKey.get(fieldKey)
          : undefined;
      const tooltipKey = EDIT_DRAWER_TOOLTIP_KEY[fieldKey];
      return (
        trackingMeta?.label ??
        (tooltipKey != null
          ? getMapImportFieldLabel(tooltipKey, t)
          : getEditDrawerLabel(fieldKey, t))
      );
    },
    [t, trackingCategoryFieldMetaByKey]
  );

  const renderDrawerTableColumnHeader = React.useCallback(
    (fieldKey: string) => {
      const labelText = resolveDrawerTableFieldLabel(fieldKey);
      const isOptional = editDrawerOptionalKeys.has(fieldKey);
      return (
        <span className="drawer-table-col-header">
          <span className="drawer-table-col-header-row">
            <span className="drawer-table-col-header-label">{labelText}</span>
          </span>
          {isOptional ? (
            <span className="drawer-table-col-optional">
              {t('organisation.optional-lowercase')}
            </span>
          ) : null}
        </span>
      );
    },
    [resolveDrawerTableFieldLabel, editDrawerOptionalKeys]
  );

  const drawerTablePerLineKeys = React.useMemo(() => {
    if (!useFieldSplitLayout) {
      return keysList as TransactionEditDrawerPerLineFieldKey[];
    }
    const allowed = new Set(
      keysList.filter((k) => !sharedFieldKeySet.has(k as string))
    );
    return buildDrawerTablePerLineFieldOrder(
      showTrackingCategoryColumns,
      allowed
    );
  }, [
    useFieldSplitLayout,
    keysList,
    sharedFieldKeySet,
    showTrackingCategoryColumns,
  ]);

  const renderTableCell = (
    originalRow: ValidatedExcelTransaction,
    rowUuid: string,
    fieldKey:
      | TransactionEditDrawerPerLineFieldKey
      | (typeof EDITABLE_ROW_KEYS)[number]
  ) => {
    const validationFieldKey = (() => {
      if (fieldKey === 'taxRateId') return 'taxName' as const;
      if (
        Object.prototype.hasOwnProperty.call(
          TRANSACTION_EDIT_REQUIRED_FIELD_LABELS,
          fieldKey
        )
      ) {
        return fieldKey as TransactionEditRequiredFieldKey;
      }
      return null;
    })();
    const isInvalid =
      validationFieldKey != null &&
      isDrawerFieldInvalid(rowUuid, validationFieldKey, false);

    const isAmountCol = DRAWER_TABLE_AMOUNT_FIELD_KEYS.has(fieldKey);
    const isDescriptionCol = fieldKey === 'description';
    const isDiscountCol = fieldKey === 'discountAmount';
    const isTaxCol = fieldKey === 'tax';
    const isTextCol = !isAmountCol;
    const draftRow = draftMap[rowUuid] ?? {};
    const hoverTitle = (() => {
      if (
        fieldKey === 'trackingOption1Id' ||
        fieldKey === 'trackingOption2Id'
      ) {
        const meta = trackingCategoryFieldMetaByKey.get(fieldKey);
        if (!meta) return '';
        const origRecord = originalRow as unknown as Record<string, unknown>;
        const draftRecord = draftRow as Record<string, unknown>;
        const raw = draftRecord[fieldKey] ?? origRecord[fieldKey];
        if (raw == null || String(raw).trim() === '') return '';
        const trimmed = String(raw).trim();
        const nameField =
          fieldKey === 'trackingOption1Id'
            ? 'trackingOption1Name'
            : 'trackingOption2Name';
        const nameRaw = draftRecord[nameField] ?? origRecord[nameField];
        return resolveTrackingOptionDisplayLabel(
          trimmed,
          nameRaw == null ? null : String(nameRaw),
          trackingCategories,
          meta.dimensionIndex
        );
      }

      return resolveDrawerFieldTooltipTitle({
        fieldKey: fieldKey as (typeof EDITABLE_ROW_KEYS)[number],
        originalRow,
        draftRow,
        ...tooltipTitleParams,
      });
    })();

    const control = (
      <EditDrawerControlWrap $fullWidth $equalColumn $invalid={isInvalid}>
        {renderField(
          originalRow,
          rowUuid,
          fieldKey as TransactionEditDrawerPerLineFieldKey,
          { tableTextWrap: gridTextWrap, tableCellMode: true }
        )}
      </EditDrawerControlWrap>
    );

    const cellClassName = [
      isTextCol ? 'drawer-table-text-col' : '',
      isAmountCol ? 'drawer-table-amount-col' : '',
      isDescriptionCol ? 'drawer-table-description-col' : '',
      isDiscountCol ? 'drawer-table-discount-col' : '',
      isTaxCol ? 'drawer-table-tax-col' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const showHoverTitle = hoverTitle.trim().length > 0;

    return (
      <div className={cellClassName || undefined}>
        {showHoverTitle ? (
          <Tooltip title={hoverTitle} mouseEnterDelay={0.4}>
            <EditDrawerControlTooltipTarget>
              {control}
            </EditDrawerControlTooltipTarget>
          </Tooltip>
        ) : (
          control
        )}
      </div>
    );
  };

  const drawerLineTableDataSource = React.useMemo((): DrawerLineTableRow[] => {
    return rowsWithIds.map((originalRow, idx) => {
      const rowUuid = String(originalRow.uuid);
      const lineLabel = String(idx + 1);
      return {
        key: rowUuid,
        lineLabel,
        originalRow,
        rowUuid,
      };
    });
  }, [rowsWithIds]);

  const drawerLineTableColumns =
    React.useMemo((): ColumnsType<DrawerLineTableRow> => {
      const fieldKeys = drawerTablePerLineKeys;
      const lineColumn: ColumnsType<DrawerLineTableRow>[number] = {
        title: '',
        key: '_line',
        dataIndex: 'lineLabel',
        width: DRAWER_TABLE_LINE_INDEX_COLUMN_WIDTH,
        align: 'center',
        className: 'drawer-table-line-index-col',
        onHeaderCell: () => ({ className: 'drawer-table-line-index-col' }),
        onCell: () => ({ className: 'drawer-table-line-index-col' }),
        render: (label: string) => (
          <span className="drawer-table-line-label">{label}</span>
        ),
      };
      const fieldColumns: ColumnsType<DrawerLineTableRow> = fieldKeys.map(
        (fieldKey) => {
          const width = resolveDrawerTableColumnWidth(
            fieldKey,
            gridColumnWidths
          );
          return {
            title: renderDrawerTableColumnHeader(fieldKey),
            key: fieldKey,
            width,
            ellipsis: fieldKey === 'description' && !gridTextWrap,
            onHeaderCell: () =>
              gridColumnWidthsLocked
                ? { width }
                : {
                    width,
                    minWidth: DRAWER_TABLE_COLUMN_RESIZE_MIN_WIDTH,
                    onResize: handleDrawerTableColumnResize(fieldKey),
                    resizeAriaLabel: t('organisation.resize-grid-column', {
                      defaultValue: 'Drag to resize column',
                    }),
                  },
            render: (_: unknown, record: DrawerLineTableRow) =>
              renderTableCell(
                record.originalRow,
                record.rowUuid,
                fieldKey as TransactionEditDrawerPerLineFieldKey
              ),
          };
        }
      );
      return [lineColumn, ...fieldColumns];
    }, [
      drawerTablePerLineKeys,
      renderDrawerTableColumnHeader,
      draftMap,
      fieldErrorsByTransactionId,
      gridTextWrap,
      gridColumnWidths,
      gridColumnWidthsLocked,
      handleDrawerTableColumnResize,
      onCreateInterpretationRule,
      renderCreateRuleLink,
      rowsWithIds.length,
      t,
    ]);

  const drawerLineTableSummary = React.useCallback(
    () => (
      <Table.Summary>
        <Table.Summary.Row>
          <Table.Summary.Cell
            index={0}
            className="drawer-table-line-index-col"
          />
          {drawerTablePerLineKeys.map((fieldKey, idx) => {
            const totalValue =
              fieldKey === 'net'
                ? documentAmountTotals.net
                : fieldKey === 'tax'
                ? documentAmountTotals.tax
                : fieldKey === 'gross'
                ? documentAmountTotals.gross
                : null;
            const isAmountTotalCol = totalValue != null;
            const className = [
              fieldKey === 'description' ? 'drawer-table-description-col' : '',
              isAmountTotalCol ? 'drawer-table-amount-col' : '',
              fieldKey === 'tax' ? 'drawer-table-tax-col' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <Table.Summary.Cell
                key={fieldKey}
                index={idx + 1}
                className={className || undefined}
              >
                {fieldKey === 'description' ? (
                  <span className="drawer-table-summary-label">
                    {t('billing.total')}
                  </span>
                ) : totalValue != null ? (
                  <span
                    className="drawer-table-summary-amount"
                    title={formatDocumentTotalAmount(totalValue)}
                  >
                    {formatDocumentTotalAmount(totalValue)}
                  </span>
                ) : null}
              </Table.Summary.Cell>
            );
          })}
        </Table.Summary.Row>
      </Table.Summary>
    ),
    [
      drawerTablePerLineKeys,
      documentAmountTotals,
      formatDocumentTotalAmount,
      t,
    ]
  );

  const drawerLineTableScrollX = React.useMemo(
    () =>
      getDrawerLineTableMinWidth(
        drawerTablePerLineKeys,
        false,
        gridColumnWidths
      ),
    [drawerTablePerLineKeys, gridColumnWidths]
  );

  const hasCustomGridColumnWidths = Object.keys(gridColumnWidths).length > 0;

  const showDrawerLineTable =
    lineViewMode === 'table' && useFieldSplitLayout && rowsWithIds.length > 0;

  const tableLayoutRef = React.useRef<HTMLDivElement | null>(null);
  const tableFrameRef = React.useRef<HTMLDivElement | null>(null);
  const deleteRailRef = React.useRef<HTMLDivElement | null>(null);
  const hoveredDeleteRowIdRef = React.useRef<string | null>(null);
  const deleteHoverClearTimerRef = React.useRef<number | null>(null);

  const setDeleteRowHover = React.useCallback((rowUuid: string | null) => {
    if (deleteHoverClearTimerRef.current != null) {
      window.clearTimeout(deleteHoverClearTimerRef.current);
      deleteHoverClearTimerRef.current = null;
    }

    const apply = (id: string | null) => {
      const rail = deleteRailRef.current;
      if (!rail) return;
      const prevId = hoveredDeleteRowIdRef.current;
      if (prevId && prevId !== id) {
        const prev = rail.querySelector(
          `[data-row-key="${CSS.escape(prevId)}"]`
        );
        prev?.setAttribute('data-active', 'false');
      }
      hoveredDeleteRowIdRef.current = id;
      if (id) {
        const next = rail.querySelector(`[data-row-key="${CSS.escape(id)}"]`);
        next?.setAttribute('data-active', 'true');
      } else if (prevId) {
        const prev = rail.querySelector(
          `[data-row-key="${CSS.escape(prevId)}"]`
        );
        prev?.setAttribute('data-active', 'false');
      }
    };

    if (rowUuid == null) {
      deleteHoverClearTimerRef.current = window.setTimeout(() => {
        apply(null);
        deleteHoverClearTimerRef.current = null;
      }, 80);
      return;
    }
    apply(rowUuid);
  }, []);

  React.useEffect(
    () => () => {
      if (deleteHoverClearTimerRef.current != null) {
        window.clearTimeout(deleteHoverClearTimerRef.current);
      }
    },
    []
  );

  React.useLayoutEffect(() => {
    if (!showDrawerLineTable || !canDeleteLines) return;
    const layout = tableLayoutRef.current;
    const frame = tableFrameRef.current;
    const rail = deleteRailRef.current;
    if (!layout || !frame || !rail) return;

    const syncRailGeometry = () => {
      const layoutRect = layout.getBoundingClientRect();
      const slots = rail.querySelectorAll(
        '[data-delete-rail-slot]'
      ) as NodeListOf<HTMLElement>;

      slots.forEach((slot) => {
        const rowKey = slot.dataset.rowKey;
        if (!rowKey) return;
        const tr = frame.querySelector(
          `.ant-table-tbody > tr[data-row-key="${CSS.escape(rowKey)}"]`
        ) as HTMLElement | null;
        if (!tr) return;
        const rowRect = tr.getBoundingClientRect();
        slot.style.top = `${rowRect.top - layoutRect.top}px`;
        slot.style.height = `${rowRect.height}px`;
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      syncRailGeometry();
    });
    resizeObserver.observe(layout);
    resizeObserver.observe(frame);
    frame
      .querySelectorAll('.ant-table-tbody > tr:not(.ant-table-measure-row)')
      .forEach((tr) => resizeObserver.observe(tr));

    syncRailGeometry();
    const raf = window.requestAnimationFrame(syncRailGeometry);
    return () => {
      window.cancelAnimationFrame(raf);
      resizeObserver.disconnect();
    };
  }, [
    showDrawerLineTable,
    canDeleteLines,
    drawerLineTableDataSource,
    gridTextWrap,
    drawerTablePerLineKeys,
    documentAmountTotals,
  ]);

  return (
    <>
      <EditDrawerSelectDropdownStyle />
      {/* PWA: portal the chrome / sticky action bar into slots outside the scroll area */}
      {pwaMobileNav && integratedDrawerHeader && pwaChromePortalTarget
        ? createPortal(integratedDrawerChromeToolbar, pwaChromePortalTarget)
        : null}
      {pwaMobileNav &&
      integratedDrawerHeader &&
      pwaActionbarPortalTarget &&
      pwaActionbar
        ? createPortal(pwaActionbar, pwaActionbarPortalTarget)
        : null}
      <div
        data-transaction-edit-drawer
        className={pwaMobileNav ? 'scope-pwa-review-drawer-form-stack' : undefined}
        style={{
          padding: integratedDrawerHeader
            ? isCompact
              ? '4px 4px 10px'
              : '8px 4px 12px'
            : isCompact
            ? '12px 16px'
            : '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: isCompact ? 10 : 14,
          flex: 1,
          minHeight: 0,
        }}
      >
        {!integratedDrawerHeader && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            {inlineFallbackFullToolbar}
          </div>
        )}

        {integratedDrawerHeader && !pwaMobileNav && (
          <div
            style={{
              width: '100%',
              marginBottom: isCompact ? 6 : 10,
              flexShrink: 0,
            }}
          >
            {densityColumnSegmentClustersOnly}
          </div>
        )}

        <TransactionEditDrawerLinesScroll
          $withDeleteGutter={showDrawerLineTable && canDeleteLines}
        >
          {isSaving ? (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--color-bg-mask, rgba(0, 0, 0, 0.45))',
                borderRadius: DRAWER_DOCUMENT_SURFACE_RADIUS_PX,
              }}
              aria-busy
              aria-live="polite"
            >
              <Spin size="large" />
            </div>
          ) : null}
          {useFieldSplitLayout &&
            anchorRow &&
            anchorUuid &&
            sharedColumnKeyChunks.length > 0 &&
            (pwaMobileNav ? (
              <div className="scope-pwa-review-drawer-document-stack">
                {pwaSummaryMeta ? (
                  <div className="pwa-card scope-pwa-review-summary-card">
                    <div className="scope-pwa-review-summary-card__head">
                      <span className="pwa-row-body">
                        <span className="pwa-row-title">{pwaSummaryMeta.title}</span>
                        {pwaSummaryMeta.postsMeta ? (
                          <span className="pwa-row-meta">
                            {pwaSummaryMeta.postsMeta}
                          </span>
                        ) : null}
                      </span>
                      <PwaChip
                        tone={pwaSummaryMeta.isReviewed ? 'ok' : 'warn'}
                        icon="review"
                      >
                        {pwaSummaryMeta.isReviewed
                          ? t('organisation.reviewed', {
                              defaultValue: 'Reviewed',
                            })
                          : t('organisation.pwa-home-stat-to-review', {
                              defaultValue: 'To review',
                            })}
                      </PwaChip>
                    </div>
                    <div className="pwa-totals">
                      <div>
                        <span>{t('transactions.net')}</span>
                        <b>
                          {formatDocumentTotalAmount(documentAmountTotals.net)}
                        </b>
                      </div>
                      <div>
                        <span>{t('transactions.tax')}</span>
                        <b>
                          {formatDocumentTotalAmount(documentAmountTotals.tax)}
                        </b>
                      </div>
                      <div>
                        <span>{t('transactions.gross')}</span>
                        <b>
                          {formatDocumentTotalAmount(
                            documentAmountTotals.gross
                          )}
                        </b>
                      </div>
                    </div>
                  </div>
                ) : null}

                {pwaDocumentFieldChunks.length > 0 ? (
                  <section className="pwa-section scope-pwa-review-drawer-section">
                    <div className="pwa-section-head">
                      <span>
                        {t('common.document', {
                          defaultValue: 'Document',
                        })}
                      </span>
                    </div>
                    <div className="pwa-card pad scope-pwa-review-drawer-fields-card">
                      <EditDrawerLineFieldsGrid
                        className="scope-pwa-review-drawer-fields-grid"
                        $columns={layoutColumnCount}
                        style={{
                          columnGap: 12,
                          rowGap: 10,
                        }}
                      >
                        {pwaDocumentFieldChunks.map((chunk, colIdx) => (
                          <EditDrawerLineFieldsColumn
                            key={`pwa-doc-${colIdx}`}
                          >
                            {renderColumn(anchorRow, anchorUuid, chunk, {
                              applyToAllLines: useGroupedDocumentFieldsLayout,
                              equalColumnLayout: true,
                            })}
                          </EditDrawerLineFieldsColumn>
                        ))}
                      </EditDrawerLineFieldsGrid>
                    </div>
                  </section>
                ) : null}

                {pwaCurrencyFieldChunks.length > 0 ? (
                  <section className="pwa-section scope-pwa-review-drawer-section">
                    <div className="pwa-section-head">
                      <span>
                        {t('organisation.currency', {
                          defaultValue: 'Currency',
                        })}
                      </span>
                    </div>
                    <div className="pwa-card pad scope-pwa-review-drawer-fields-card">
                      <EditDrawerLineFieldsGrid
                        className="scope-pwa-review-drawer-fields-grid"
                        $columns={layoutColumnCount}
                        style={{
                          columnGap: 12,
                          rowGap: 10,
                        }}
                      >
                        {pwaCurrencyFieldChunks.map((chunk, colIdx) => (
                          <EditDrawerLineFieldsColumn
                            key={`pwa-cur-${colIdx}`}
                          >
                            {renderColumn(anchorRow, anchorUuid, chunk, {
                              applyToAllLines: useGroupedDocumentFieldsLayout,
                              equalColumnLayout: true,
                            })}
                          </EditDrawerLineFieldsColumn>
                        ))}
                      </EditDrawerLineFieldsGrid>
                    </div>
                  </section>
                ) : null}
              </div>
            ) : (
              <div
                style={{
                  marginBottom: isCompact ? 14 : 18,
                  padding: isCompact ? '10px 12px' : '14px 16px',
                  borderRadius: DRAWER_DOCUMENT_SURFACE_RADIUS_PX,
                  border: DRAWER_DOCUMENT_SURFACE_BORDER,
                  background: DRAWER_DOCUMENT_SURFACE_BACKGROUND,
                }}
              >
                {useGroupedDocumentFieldsLayout ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '4px 16px',
                      marginBottom: isCompact ? 8 : 12,
                      fontSize: isCompact ? 11 : 12,
                      lineHeight: 1.5,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        color: 'var(--color-text-dark, #042a2a)',
                        flexShrink: 0,
                      }}
                    >
                      Document details
                    </span>
                    <span
                      style={{
                        color: 'var(--color-text-muted, #5a6a6a)',
                        minWidth: 0,
                      }}
                    >
                      {t('organisation.transactions-totals-summary', {
                        totalsLabel: t('vat-forms.totals'),
                        selectedSuffix: '',
                        netLabel: t('transactions.net'),
                        net: formatDocumentTotalAmount(
                          documentAmountTotals.net
                        ),
                        taxLabel: t('transactions.tax'),
                        tax: formatDocumentTotalAmount(
                          documentAmountTotals.tax
                        ),
                        totalLabel: t('transactions.gross'),
                        total: formatDocumentTotalAmount(
                          documentAmountTotals.gross
                        ),
                      })}
                    </span>
                  </div>
                ) : null}
                {useLineFieldsEqualColumns ? (
                  <EditDrawerLineFieldsGrid
                    $columns={layoutColumnCount}
                    style={{
                      columnGap: isCompact ? 14 : 24,
                      rowGap: isCompact ? 8 : 10,
                    }}
                  >
                    {sharedColumnKeyChunks.map((chunk, colIdx) => (
                      <EditDrawerLineFieldsColumn key={`shared-${colIdx}`}>
                        {renderColumn(anchorRow, anchorUuid, chunk, {
                          applyToAllLines: useGroupedDocumentFieldsLayout,
                          equalColumnLayout: true,
                        })}
                      </EditDrawerLineFieldsColumn>
                    ))}
                  </EditDrawerLineFieldsGrid>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${layoutColumnCount}, minmax(0, 1fr))`,
                      columnGap: isCompact ? 14 : 24,
                      rowGap: isCompact ? 8 : 10,
                      alignItems: 'stretch',
                    }}
                  >
                    {sharedColumnKeyChunks.map((chunk, colIdx) => (
                      <React.Fragment key={`shared-${colIdx}`}>
                        {renderColumn(anchorRow, anchorUuid, chunk, {
                          applyToAllLines: useGroupedDocumentFieldsLayout,
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            ))}

          {showDrawerLineTable ? (
            <TransactionEditDrawerTableLayout ref={tableLayoutRef}>
              <TransactionEditDrawerTableWrap
                ref={tableFrameRef}
                $textWrap={gridTextWrap}
                $columnWidthsLocked={gridColumnWidthsLocked}
              >
                <TransactionEditDrawerTableToolbar className="transaction-edit-drawer-table-toolbar">
                  <Tooltip
                    title={
                      gridColumnWidthsLocked
                        ? t('organisation.unlock-grid-column-widths', {
                            defaultValue: 'Unlock column widths',
                          })
                        : t('organisation.lock-grid-column-widths', {
                            defaultValue: 'Lock column widths',
                          })
                    }
                  >
                    <span>
                      <IconButton
                        icon={
                          gridColumnWidthsLocked ? (
                            <LockOutlined />
                          ) : (
                            <UnlockOutlined />
                          )
                        }
                        onClick={() =>
                          setGridColumnWidthsLocked((prev) => !prev)
                        }
                        disabled={isSaving}
                        aria-pressed={gridColumnWidthsLocked}
                        aria-label={
                          gridColumnWidthsLocked
                            ? t('organisation.unlock-grid-column-widths', {
                                defaultValue: 'Unlock column widths',
                              })
                            : t('organisation.lock-grid-column-widths', {
                                defaultValue: 'Lock column widths',
                              })
                        }
                        style={
                          gridColumnWidthsLocked
                            ? {
                                color: 'var(--color-primary, #41aaaf)',
                                borderColor: 'var(--color-primary, #41aaaf)',
                                background: 'rgba(65, 170, 175, 0.12)',
                              }
                            : undefined
                        }
                      />
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={t('organisation.reset-grid-column-widths', {
                      defaultValue: 'Reset column widths',
                    })}
                  >
                    <span>
                      <IconButton
                        icon={<ColumnWidthOutlined />}
                        onClick={resetDrawerTableColumnWidths}
                        disabled={
                          isSaving ||
                          gridColumnWidthsLocked ||
                          !hasCustomGridColumnWidths
                        }
                        aria-label={t('organisation.reset-grid-column-widths', {
                          defaultValue: 'Reset column widths',
                        })}
                        style={
                          hasCustomGridColumnWidths && !gridColumnWidthsLocked
                            ? {
                                color: 'var(--color-primary, #41aaaf)',
                                borderColor: 'var(--color-primary, #41aaaf)',
                                background: 'rgba(65, 170, 175, 0.12)',
                              }
                            : undefined
                        }
                      />
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      gridTextWrap
                        ? t('organisation.truncate-long-text-in-grid-cells')
                        : t('organisation.wrap-long-text-in-grid-cells')
                    }
                  >
                    <span>
                      <IconButton
                        icon={<ColumnHeightOutlined />}
                        onClick={() => setGridTextWrap((prev) => !prev)}
                        disabled={isSaving}
                        aria-pressed={gridTextWrap}
                        aria-label={
                          gridTextWrap
                            ? t('organisation.truncate-long-text-in-grid-cells')
                            : t('organisation.wrap-long-text-in-grid-cells')
                        }
                        style={
                          gridTextWrap
                            ? {
                                color: 'var(--color-primary, #41aaaf)',
                                borderColor: 'var(--color-primary, #41aaaf)',
                                background: 'rgba(65, 170, 175, 0.12)',
                              }
                            : undefined
                        }
                      />
                    </span>
                  </Tooltip>
                </TransactionEditDrawerTableToolbar>
                <Table<DrawerLineTableRow>
                  className="transaction-edit-drawer-lines-table"
                  tableLayout="fixed"
                  size="small"
                  pagination={false}
                  scroll={{ x: drawerLineTableScrollX }}
                  dataSource={drawerLineTableDataSource}
                  columns={drawerLineTableColumns}
                  components={{
                    header: {
                      cell: DrawerLineTableResizableTitle,
                    },
                  }}
                  summary={drawerLineTableSummary}
                  onRow={(record) => ({
                    onMouseEnter: () => setDeleteRowHover(record.rowUuid),
                    onMouseLeave: () => setDeleteRowHover(null),
                  })}
                />
              </TransactionEditDrawerTableWrap>
              {canDeleteLines ? (
                <TransactionEditDrawerDeleteRail ref={deleteRailRef}>
                  {drawerLineTableDataSource.map((record) => (
                    <TransactionEditDrawerDeleteRailSlot
                      key={record.rowUuid}
                      data-delete-rail-slot
                      data-row-key={record.rowUuid}
                      onMouseEnter={() => setDeleteRowHover(record.rowUuid)}
                      onMouseLeave={() => setDeleteRowHover(null)}
                    >
                      <Tooltip title={t('organisation.delete-transaction')}>
                        <button
                          type="button"
                          className="drawer-table-delete-btn"
                          disabled={isSaving}
                          onClick={() => setLinePendingDelete(record.rowUuid)}
                          aria-label={t('organisation.delete-transaction')}
                        >
                          <DeleteOutlined />
                        </button>
                      </Tooltip>
                    </TransactionEditDrawerDeleteRailSlot>
                  ))}
                </TransactionEditDrawerDeleteRail>
              ) : null}
            </TransactionEditDrawerTableLayout>
          ) : pwaMobileNav ? (
            <div className="scope-pwa-review-drawer-document-stack">
              {rowsWithIds.map((originalRow, idx) => {
                const rowUuid = String(originalRow.uuid);
                const sectionTitle = useGroupedDocumentFieldsLayout
                  ? t('organisation.line-n', {
                      defaultValue: 'Line {{n}}',
                      n: idx + 1,
                    })
                  : useSingleLineSplitLayout
                    ? t('organisation.details', { defaultValue: 'Details' })
                    : null;
                const lineFields = useLineFieldsEqualColumns ? (
                  <EditDrawerLineFieldsGrid
                    className="scope-pwa-review-drawer-fields-grid"
                    $columns={layoutColumnCount}
                    style={{ columnGap: 12, rowGap: 10 }}
                  >
                    {columnKeyChunks.map((chunk, colIdx) => (
                      <EditDrawerLineFieldsColumn key={colIdx}>
                        {renderColumn(originalRow, rowUuid, chunk, {
                          equalColumnLayout: true,
                        })}
                      </EditDrawerLineFieldsColumn>
                    ))}
                  </EditDrawerLineFieldsGrid>
                ) : (
                  <div
                    className="scope-pwa-review-drawer-fields-grid"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${layoutColumnCount}, minmax(0, 1fr))`,
                      columnGap: 12,
                      rowGap: 10,
                      alignItems: 'stretch',
                    }}
                  >
                    {columnKeyChunks.map((chunk, colIdx) => (
                      <React.Fragment key={colIdx}>
                        {renderColumn(originalRow, rowUuid, chunk)}
                      </React.Fragment>
                    ))}
                  </div>
                );
                return (
                  <section
                    key={rowUuid}
                    className="pwa-section scope-pwa-review-drawer-section"
                  >
                    {sectionTitle ? (
                      <div className="pwa-section-head">
                        <span>{sectionTitle}</span>
                        {useGroupedDocumentFieldsLayout && canDeleteLines ? (
                          <button
                            type="button"
                            className="scope-pwa-review-drawer-section-action"
                            onClick={() => setLinePendingDelete(rowUuid)}
                            aria-label={t('organisation.delete-transaction')}
                          >
                            <PwaIcon name="trash" size={16} stroke={2} />
                            {t('common.delete', { defaultValue: 'Delete' })}
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="pwa-card pad scope-pwa-review-drawer-fields-card">
                      {lineFields}
                    </div>
                  </section>
                );
              })}
            </div>
          ) : (
            rowsWithIds.map((originalRow, idx) => {
              const rowUuid = String(originalRow.uuid);
              return (
                <div
                  key={rowUuid}
                  style={{
                    marginBottom:
                      idx < rowsWithIds.length - 1 ? (isCompact ? 14 : 22) : 0,
                    paddingBottom:
                      idx < rowsWithIds.length - 1 ? (isCompact ? 12 : 18) : 0,
                    borderBottom:
                      idx < rowsWithIds.length - 1
                        ? DRAWER_DOCUMENT_SURFACE_BORDER
                        : undefined,
                  }}
                >
                  {useGroupedDocumentFieldsLayout && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        marginBottom: isCompact ? 6 : 10,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: isCompact ? 11 : 12,
                          color: 'var(--color-primary, #41aaaf)',
                        }}
                      >
                        Line {idx + 1}
                      </div>
                      {canDeleteLines ? (
                        <Tooltip title={t('organisation.delete-transaction')}>
                          <span>
                            <IconButton
                              icon={
                                <DeleteOutlined
                                  style={{
                                    color: 'var(--btn-warning-text, #d13131)',
                                  }}
                                />
                              }
                              onClick={() => setLinePendingDelete(rowUuid)}
                              aria-label={t('organisation.delete-transaction')}
                            />
                          </span>
                        </Tooltip>
                      ) : null}
                    </div>
                  )}
                  {useSingleLineSplitLayout && (
                    <div
                      style={{
                        fontWeight: 600,
                        marginBottom: isCompact ? 6 : 10,
                        fontSize: isCompact ? 11 : 12,
                        color: 'var(--color-primary, #41aaaf)',
                      }}
                    >
                      Details
                    </div>
                  )}
                  {useLineFieldsEqualColumns ? (
                    <EditDrawerLineFieldsGrid
                      $columns={layoutColumnCount}
                      style={{
                        columnGap: isCompact ? 14 : 24,
                        rowGap: isCompact ? 8 : 10,
                      }}
                    >
                      {columnKeyChunks.map((chunk, colIdx) => (
                        <EditDrawerLineFieldsColumn key={colIdx}>
                          {renderColumn(originalRow, rowUuid, chunk, {
                            equalColumnLayout: true,
                          })}
                        </EditDrawerLineFieldsColumn>
                      ))}
                    </EditDrawerLineFieldsGrid>
                  ) : (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${layoutColumnCount}, minmax(0, 1fr))`,
                        columnGap: isCompact ? 14 : 24,
                        rowGap: isCompact ? 8 : 10,
                        alignItems: 'stretch',
                      }}
                    >
                      {columnKeyChunks.map((chunk, colIdx) => (
                        <React.Fragment key={colIdx}>
                          {renderColumn(originalRow, rowUuid, chunk)}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </TransactionEditDrawerLinesScroll>
      </div>
      <ConfirmModal
        open={pendingLeave != null}
        title={t('organisation.transactions-unsaved-row-title')}
        onCancel={handleCancelLeave}
        size="compact"
        showCancel={false}
        secondaryAction={{
          label: t('organisation.transactions-unsaved-row-discard'),
          onClick: handleDiscardAndLeave,
        }}
        primaryAction={{
          label: t('common.save'),
          onClick: handleSaveAndLeave,
        }}
      >
        <p style={{ margin: 0 }}>
          {t('organisation.transactions-unsaved-drawer-description')}
        </p>
      </ConfirmModal>
      <ConfirmModal
        open={linePendingDelete != null}
        title={t('organisation.delete-transaction')}
        onCancel={() => setLinePendingDelete(null)}
        size="compact"
        zIndex={1300}
        cancelAction={{
          label: t('common.cancel'),
          onClick: () => setLinePendingDelete(null),
        }}
        primaryAction={{
          label: t('common.yes'),
          onClick: handleConfirmDeleteLine,
          variant: 'danger',
        }}
      >
        <p style={{ margin: 0 }}>
          {t('organisation.delete-transaction-description')}
        </p>
        <p
          style={{
            margin: '12px 0 0',
            fontSize: 12,
            color: 'var(--color-text-muted, #5a6a6a)',
          }}
        >
          {t('organisation.delete-transaction-hint')}
        </p>
      </ConfirmModal>
    </>
  );
};

/** Props for the combined edit drawer + attachment viewer (used when opening drawer from attachment flow). */
export interface TransactionEditDrawerWithAttachmentsProps {
  transactionId: string;
  transactionRef: string;
  /** All lines under the same uploaded document (excelMetadataId / importId) for one-drawer editing. */
  transactions: ValidatedExcelTransaction[];
  currencyOptions: { label: string; value: string }[];
  countryOptions: { label: string; value: string }[];
  sourceTypeOptions: { label: string; value: string }[];
  accountNameOptions: { label: string; value: string }[];
  accountNameOptionsGrouped: {
    label: string;
    options: { label: string; value: string }[];
  }[];
  transactionItemOptions?: TransactionItemOption[];
  taxNameOptions: { label: string; value: string }[];
  contactOptions: { label: string; value: string }[];
  contactTypeByContactId?: Map<string, string>;
  organisationId: string;
  connectionId: string;
  organisation: Organisation | undefined;
  attachmentSourceType: string;
  currentPage: number;
  currentPageSize: number;
  effectivePageSize: number;
  currentFiltersState: Record<string, unknown> | undefined;
  selectedImportIds: string[];
  onBulkSave: (
    payloads: TransactionDrawerSavePayload[],
    postAfterSave?: { status?: ProviderPostStatus }
  ) => TransactionDrawerBulkSaveResult;
  onCancel: () => void;
  onCloseAttachments: () => void;
  onUploadAttachment: (file: File) => void;
  /** Ref updated each tab render — navigate drawer prev/next to another table row/document. */
  documentNavigateRef: React.RefObject<
    (record: Record<string, unknown>) => void
  >;
  /** For non-Excel providers, drawer prev/next may navigate by file list instead of current rows. */
  fileNavigationIds?: string[];
  currentFileNavigationId?: string;
  onNavigateToFile?: (fileId: string) => void;
  /** @deprecated Navigation is computed live from Redux; kept for callers that still pass these. */
  hasPrevious?: boolean;
  hasNext?: boolean;
  onNavigateToPrevious?: () => void;
  onNavigateToNext?: () => void;
  navigationMode?: 'transaction' | 'document';
  showTrackingCategoryColumns?: boolean;
  trackingCategories?: TrackingCategory[];
  showEditInDrawerSetting?: boolean;
  onEditModeChange?: (mode: TransactionEditMode) => void;
  handlePreferences?: (
    action: PreferenceActions,
    preference: Preference
  ) => unknown;
  /** Opens the transactions-tab rescan confirm modal for this file id. */
  onRequestRescan?: (excelMetadataId: string) => void;
  onCreateInterpretationRule?: (
    draft: Partial<InterpretationRuleDraft>
  ) => void;
  onAfterSuccessfulSave?: (payloads: TransactionDrawerSavePayload[]) => void;
  onDeleteLine?: (transactionUuid: string) => void;
  onActivity?: () => void;
}

/** Drawer content: edit form (2 cols) + attachment viewer embedded below. */
export const TransactionEditDrawerWithAttachments: React.FC<
  TransactionEditDrawerWithAttachmentsProps
> = (props) => {
  const {
    transactionId,
    transactionRef,
    transactions,
    currencyOptions,
    countryOptions,
    sourceTypeOptions,
    accountNameOptions,
    accountNameOptionsGrouped,
    onBulkSave,
    onCancel,
    onCloseAttachments,
    onUploadAttachment,
    documentNavigateRef,
    contactOptions,
    contactTypeByContactId,
    fileNavigationIds,
    currentFileNavigationId,
    onNavigateToFile,
    onRequestRescan,
    onActivity,
  } = props;
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const leaveGuardRef = React.useRef<((proceed: () => void) => void) | null>(
    null
  );
  const lastFetchedAttachments = useSelector(
    getLastFetchedTransactionAttachments
  );
  const attachmentLoading = useSelector(getVatAttachmentLoading);
  const allExcelTransactionsPage = useSelector(getAllExcelTransactionsPage);

  const navigateDrawerToRecord = React.useCallback(
    (record: Record<string, unknown>) => {
      documentNavigateRef.current?.(record);
    },
    [documentNavigateRef]
  );

  const drawerNavigationTableRecords = React.useMemo(() => {
    if (allExcelTransactionsPage?.organisationId !== props.organisationId) {
      return [];
    }
    return buildDrawerNavigationTableRecords(
      allExcelTransactionsPage?.transactions
    );
  }, [allExcelTransactionsPage, props.organisationId]);

  const resolvedLiveTransactions = React.useMemo(() => {
    const pageTx = allExcelTransactionsPage?.transactions;
    if (!pageTx?.length) return null;
    const byUuid = new Map(
      pageTx
        .filter((row) => row.uuid)
        .map((row) => [String(row.uuid), row] as const)
    );
    const resolved = transactions
      .map((row) => (row.uuid ? byUuid.get(String(row.uuid)) : undefined))
      .filter((row): row is ValidatedExcelTransaction => row != null);
    if (resolved.length !== transactions.length) return null;
    // Soft-deleted lines stay in the page payload; keep only live lines in the drawer.
    return resolved.filter((row) => !isTransactionDeletedRow(row));
  }, [transactions, allExcelTransactionsPage]);
  const [stableLiveTransactions, setStableLiveTransactions] =
    React.useState<ValidatedExcelTransaction[]>(() =>
      transactions.filter((row) => !isTransactionDeletedRow(row))
    );
  const transactionIdsSignature = React.useMemo(
    () => transactions.map((row) => String(row.uuid ?? '')).join('|'),
    [transactions]
  );
  React.useEffect(() => {
    setStableLiveTransactions(
      transactions.filter((row) => !isTransactionDeletedRow(row))
    );
  }, [transactionId, transactionIdsSignature]);
  React.useEffect(() => {
    if (!resolvedLiveTransactions) return;
    setStableLiveTransactions(resolvedLiveTransactions);
  }, [resolvedLiveTransactions]);
  const liveTransactions = resolvedLiveTransactions ?? stableLiveTransactions;
  const navigationSeedOriginal = React.useMemo(() => {
    const fromLive = liveTransactions.find(
      (row) => row.uuid && String(row.uuid) === String(transactionId)
    );
    return fromLive ?? transactions[0];
  }, [liveTransactions, transactionId, transactions]);

  const documentNavigation = React.useMemo(() => {
    const isExcelTransactionRow =
      navigationSeedOriginal?.isExcelTransaction === true;
    if (
      !isExcelTransactionRow &&
      currentFileNavigationId &&
      fileNavigationIds?.length &&
      onNavigateToFile
    ) {
      const currentIndex = fileNavigationIds.findIndex(
        (id) => id === currentFileNavigationId
      );
      return {
        hasPrevious: currentIndex > 0,
        hasNext:
          currentIndex >= 0 && currentIndex < fileNavigationIds.length - 1,
        onNavigateToPrevious: () => {
          if (currentIndex <= 0) return;
          onNavigateToFile(fileNavigationIds[currentIndex - 1]);
        },
        onNavigateToNext: () => {
          if (
            currentIndex < 0 ||
            currentIndex >= fileNavigationIds.length - 1
          ) {
            return;
          }
          onNavigateToFile(fileNavigationIds[currentIndex + 1]);
        },
        mode: 'document' as const,
        currentIndex: currentIndex >= 0 ? currentIndex + 1 : undefined,
        totalCount: fileNavigationIds.length,
      };
    }

    const nav = computeDrawerDocumentNavigation({
      tableRecords: drawerNavigationTableRecords,
      attachmentsTransactionId: transactionId,
      seedOriginal: navigationSeedOriginal,
      navigateToRecord: navigateDrawerToRecord,
    });
    return {
      hasPrevious: nav.hasPrevious,
      hasNext: nav.hasNext,
      onNavigateToPrevious: nav.onNavigateToPrevious,
      onNavigateToNext: nav.onNavigateToNext,
      mode: nav.navigationMode,
    };
  }, [
    currentFileNavigationId,
    fileNavigationIds,
    onNavigateToFile,
    drawerNavigationTableRecords,
    transactionId,
    navigationSeedOriginal,
    navigateDrawerToRecord,
  ]);

  const attachments = React.useMemo(() => {
    if (lastFetchedAttachments?.transactionId === transactionId) {
      return lastFetchedAttachments.attachments ?? [];
    }
    return [];
  }, [transactionId, lastFetchedAttachments]);

  // Avoid empty→spin→content flash while Redux catches up after opening Review.
  const attachmentViewerLoading =
    attachmentLoading ||
    (Boolean(transactionId) &&
      lastFetchedAttachments?.transactionId !== transactionId);

  const onCloseViewer = React.useCallback(() => {
    const proceed = () => {
      dispatch(turnDrawerOff());
      onCloseAttachments();
    };
    const guard = leaveGuardRef.current;
    if (guard) {
      guard(proceed);
      return;
    }
    proceed();
  }, [dispatch, onCloseAttachments]);

  const pwaChromeDragZoneRef = React.useRef<HTMLDivElement | null>(null);
  const pwaChromeDragClose = usePwaBottomSheetDragClose(
    pwaMobileNav,
    onCloseViewer,
    pwaChromeDragZoneRef
  );

  const attachmentViewerProviderKey = React.useMemo(
    () => resolveAttachmentViewerProviderLogoKey(props.organisation),
    [props.organisation]
  );
  const attachmentViewerSourceUrl = React.useMemo(
    () =>
      resolveProviderExternalLinkForDocumentRows(
        props.organisation,
        transactions
      ),
    [props.organisation, transactions]
  );

  const drawerRescanState = React.useMemo(() => {
    const fileId = String(currentFileNavigationId ?? '').trim();
    if (!fileId) return { show: false as const };
    const provider = props.organisation?.providerName?.toLowerCase() ?? '';
    if (provider === Provider.EXCEL.toLowerCase()) {
      return { show: false as const };
    }
    const file = (props.organisation?.excelFilesMetadata ?? []).find(
      (item) => String(item.excelMetadataId ?? '').trim() === fileId
    );
    if (!file || file.isPosted) return { show: false as const };
    return { show: true as const, fileId };
  }, [currentFileNavigationId, props.organisation]);

  const handleDrawerRescan = React.useCallback(() => {
    if (!drawerRescanState.show) return;
    onRequestRescan?.(drawerRescanState.fileId);
  }, [drawerRescanState, onRequestRescan]);

  // Sticky Save/Rescan action bar is portalled from the form content.
  const actionbarPortalTargetRef = React.useRef<HTMLDivElement | null>(null);
  const [actionbarPortalReady, setActionbarPortalReady] = React.useState(false);
  const actionbarTargetCallback = React.useCallback(
    (node: HTMLDivElement | null) => {
      actionbarPortalTargetRef.current = node;
    },
    []
  );

  React.useLayoutEffect(() => {
    const actionbarReady = Boolean(actionbarPortalTargetRef.current);
    setActionbarPortalReady((prev) =>
      prev === actionbarReady ? prev : actionbarReady
    );
  });

  const pwaDetailContactName = String(
    (liveTransactions[0] as { contactName?: string | null } | undefined)
      ?.contactName ?? ''
  ).trim();
  const pwaDetailUploadedRaw = (
    liveTransactions[0] as { uploadDate?: number | string } | undefined
  )?.uploadDate;
  const pwaDetailUploadedLabel = pwaDetailUploadedRaw
    ? formatTransactionTableDate(pwaDetailUploadedRaw, i18n.language)
    : '';
  const pwaDetailIdx = documentNavigation?.currentIndex;
  const pwaDetailTotal = documentNavigation?.totalCount;
  const pwaDetailPosition =
    pwaDetailIdx != null && pwaDetailTotal != null && pwaDetailTotal > 0
      ? t('organisation.pwa-review-document-position', {
          defaultValue: 'Document {{current}} of {{total}}',
          current: pwaDetailIdx,
          total: pwaDetailTotal,
        })
      : t('organisation.review-document-drawer-title', {
          defaultValue: 'Review document',
        });
  const pwaDetailSubtitle = pwaDetailUploadedLabel
    ? `${pwaDetailPosition} · ${pwaDetailUploadedLabel}`
    : pwaDetailPosition;

  const handlePwaDetailClose = React.useCallback(() => {
    const guard = leaveGuardRef.current;
    if (guard) {
      guard(() => {
        onCancel();
        onCloseAttachments();
      });
      return;
    }
    onCancel();
    onCloseAttachments();
  }, [onCancel, onCloseAttachments]);

  const pwaDetailMoreItems: MenuProps['items'] = [
    ...(documentNavigation
      ? [
          {
            key: 'prev',
            label:
              documentNavigation.mode === 'transaction'
                ? t('organisation.previous-transaction')
                : t('organisation.previous-document'),
            disabled: !documentNavigation.hasPrevious,
            onClick: () => documentNavigation.onNavigateToPrevious(),
          },
          {
            key: 'next',
            label:
              documentNavigation.mode === 'transaction'
                ? t('organisation.next-transaction')
                : t('organisation.next-document'),
            disabled: !documentNavigation.hasNext,
            onClick: () => documentNavigation.onNavigateToNext(),
          },
        ]
      : []),
    ...(drawerRescanState.show
      ? [
          {
            key: 'rescan',
            label: t('organisation.file-rescan'),
            onClick: () => handleDrawerRescan(),
          },
        ]
      : []),
    {
      key: 'close',
      label: t('common.close'),
      onClick: () => handlePwaDetailClose(),
    },
  ];

  return (
    <div
      data-shell={pwaMobileNav ? 'pwa' : undefined}
      className={
        pwaMobileNav
          ? 'scope-pwa-review-drawer has-actionbar'
          : undefined
      }
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100%',
        background: pwaMobileNav
          ? 'var(--gt-canvas, var(--color-surface-base, #0C1718))'
          : 'var(--color-layout-bg, #E9F6F7)',
      }}
    >
      {/* PWA: TopBar chrome owned by the shell (not portalled from form). */}
      {pwaMobileNav && (
        <div
          ref={pwaChromeDragZoneRef}
          className="scope-pwa-review-drawer__chrome scope-pwa-bottom-sheet__drag-zone"
          aria-label={t('organisation.pull-down-to-close')}
          {...pwaChromeDragClose}
        >
          <div className="scope-pwa-bottom-sheet__drag-handle" aria-hidden />
          <div className="txn-review-drawer-chrome txn-review-drawer-chrome--pwa txn-review-drawer-chrome--pwa-detail">
            <div className="txn-review-drawer-chrome__detail-top">
              <button
                type="button"
                className="pwa-iconbtn subtle"
                aria-label={t('common.back', { defaultValue: 'Back' })}
                onClick={handlePwaDetailClose}
              >
                <PwaIcon name="back" />
              </button>
              <div className="txn-review-drawer-chrome__detail-title">
                <strong>
                  {pwaDetailContactName || (
                    <span style={{ color: 'var(--gt-warn, #c48a00)' }}>
                      {t('organisation.pwa-review-supplier-missing', {
                        defaultValue: 'Supplier not found',
                      })}
                    </span>
                  )}
                </strong>
                <span>{pwaDetailSubtitle}</span>
              </div>
              {onActivity ? (
                <button
                  type="button"
                  className="pwa-iconbtn subtle"
                  aria-label={t('organisation.reconciliation-activity', {
                    defaultValue: 'Activity',
                  })}
                  onClick={onActivity}
                >
                  <PwaIcon name="history" />
                </button>
              ) : null}
              <Dropdown
                trigger={['click']}
                placement="bottomRight"
                menu={{ items: pwaDetailMoreItems }}
              >
                <button
                  type="button"
                  className="pwa-iconbtn subtle"
                  aria-label={t('common.more-actions')}
                >
                  <PwaIcon name="dots" />
                </button>
              </Dropdown>
            </div>
          </div>
        </div>
      )}
      <div
        className={pwaMobileNav ? 'scope-pwa-review-drawer__split' : undefined}
        style={{
          display: 'flex',
          flexDirection: pwaMobileNav ? 'column' : 'row',
          gap: pwaMobileNav ? 0 : 24,
          padding: pwaMobileNav ? '0' : '0 4px',
          minWidth: 0,
          minHeight: 0,
          alignItems: pwaMobileNav ? 'stretch' : 'flex-start',
          flex: 1,
          overflowY: pwaMobileNav ? 'auto' : undefined,
          WebkitOverflowScrolling: pwaMobileNav ? 'touch' : undefined,
        }}
      >
        {/* Left: attachment viewer — content height, capped at drawer */}
        <div
          className={pwaMobileNav ? 'scope-pwa-review-drawer__viewer' : undefined}
          style={{
            flex: pwaMobileNav ? '0 0 auto' : '0 0 540px',
            minWidth: 0,
            minHeight: pwaMobileNav ? 0 : 500,
            maxHeight: pwaMobileNav ? undefined : 'calc(100vh - 132px)',
            width: pwaMobileNav ? '100%' : undefined,
            display: 'flex',
            flexDirection: 'column',
            alignSelf: pwaMobileNav ? 'auto' : 'flex-start',
          }}
        >
          <AttachmentViewer
            embedded
            standaloneFullHeight={!pwaMobileNav}
            onClose={onCloseViewer}
            transactionReference={transactionRef || undefined}
            attachments={attachments}
            attachmentLoading={attachmentViewerLoading}
            providerName={attachmentViewerProviderKey}
            attachmentSourceUrl={attachmentViewerSourceUrl}
            showUpload={
              props.organisation?.providerName?.toLowerCase() ===
                Provider.EXCEL.toLowerCase() ||
              props.organisation?.providerName?.toLowerCase() ===
                Provider.RECEIPTS.toLowerCase()
            }
            onUploadAttachment={onUploadAttachment}
            currentUser={undefined}
            currentOrganisation={props.organisation as any}
            hasAdvancedUserRights={undefined}
          />
        </div>
        {/* Right: edit form (multi-line same document, configurable columns) */}
        <div
          className={pwaMobileNav ? 'scope-pwa-review-drawer__form' : undefined}
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            maxHeight: pwaMobileNav ? undefined : 'calc(100vh - 132px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <TransactionEditDrawerContent
            transactions={liveTransactions}
            reviewSeedTransactions={transactions}
            currencyOptions={currencyOptions}
            countryOptions={countryOptions}
            sourceTypeOptions={sourceTypeOptions}
            accountNameOptions={accountNameOptions}
            accountNameOptionsGrouped={accountNameOptionsGrouped}
            transactionItemOptions={props.transactionItemOptions}
            taxNameOptions={props.taxNameOptions}
            organisationTaxRates={
              (props.organisation?.taxRatesUnique &&
              props.organisation.taxRatesUnique.length > 0
                ? props.organisation.taxRatesUnique
                : props.organisation?.taxRates) ?? []
            }
            contactOptions={contactOptions}
            contactTypeByContactId={contactTypeByContactId}
            onBulkSave={onBulkSave}
            onCancel={onCancel}
            providerName={props.organisation?.providerName}
            showTrackingCategoryColumns={props.showTrackingCategoryColumns}
            trackingCategories={
              props.trackingCategories ??
              props.organisation?.trackingCategories ??
              []
            }
            integratedDrawerHeader
            documentNavigation={documentNavigation}
            showEditInDrawerSetting={props.showEditInDrawerSetting}
            onEditModeChange={props.onEditModeChange}
            handlePreferences={props.handlePreferences}
            showRescan={drawerRescanState.show}
            onRescan={handleDrawerRescan}
            rescanLabel={t('organisation.file-rescan')}
            rescanTooltip={t('organisation.file-rescan-tooltip')}
            onCreateInterpretationRule={props.onCreateInterpretationRule}
            onAfterSuccessfulSave={props.onAfterSuccessfulSave}
            onDeleteLine={props.onDeleteLine}
            leaveGuardRef={leaveGuardRef}
            onActivity={onActivity}
            /* PWA TopBar is owned by the shell above; only action bar is portalled. */
            pwaChromePortalTarget={null}
            pwaActionbarPortalTarget={
              actionbarPortalReady ? actionbarPortalTargetRef.current : null
            }
          />
        </div>
      </div>
      {pwaMobileNav ? (
        <div
          className="scope-pwa-review-drawer__actionbar"
          ref={actionbarTargetCallback}
        />
      ) : null}
    </div>
  );
};
